import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, X, XCircle, Plus, Image, Layout, Eye, ChevronDown, Zap, Printer, RefreshCw, RotateCcw, Copy, Palette, List, Edit2, Check, Search } from 'lucide-react';
import { createAndPlaceArtwork, updateArtworkPosition, swapArtworkDimensions, searchArtistArtwork, addArtworkToCollection, getArtistArtworkByUuid } from '../utils/api';
import '../styles/TopDownWallPlanner.css';

// Tools dropdown sub-component for the top-down planner header
const ToolsDropdown = ({ toolsActions, onClose, onDone }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const menuBtnStyle = {
    width: '100%', padding: '12px 16px', display: 'flex', alignItems: 'center',
    gap: '10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', textAlign: 'left'
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="mode-toggle-btn"
        onClick={() => setOpen(!open)}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#7c3aed', color: '#fff' }}
      >
        <Palette size={16} />
        <span>Tools</span>
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: '4px',
          backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          minWidth: '220px', zIndex: 1000, overflow: 'hidden'
        }}>
          {toolsActions.onViewIn3D && (
            <button style={menuBtnStyle} onClick={() => { toolsActions.onViewIn3D(); setOpen(false); }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <Zap size={18} style={{ color: '#7c3aed' }} /><span>3D Gallery View</span>
            </button>
          )}
          {toolsActions.onGenerateLabels && (
            <button style={menuBtnStyle} onClick={() => { toolsActions.onGenerateLabels(); setOpen(false); }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
              </svg><span>Generate Labels</span>
            </button>
          )}
          {toolsActions.onPrintWallLayouts && (
            <button style={menuBtnStyle} onClick={() => { toolsActions.onPrintWallLayouts(); setOpen(false); }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <Printer size={18} style={{ color: '#7c3aed' }} /><span>Print Wall Layouts</span>
            </button>
          )}
          {toolsActions.onPrintEaselLayouts && (
            <button style={menuBtnStyle} onClick={() => { toolsActions.onPrintEaselLayouts(); setOpen(false); }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <Printer size={18} style={{ color: '#92400e' }} /><span>Print Easel Layouts</span>
            </button>
          )}
          {toolsActions.onCopyWallsFromPrevious && (
            <button style={menuBtnStyle} onClick={() => { toolsActions.onCopyWallsFromPrevious(); setOpen(false); }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <Copy size={18} style={{ color: '#7c3aed' }} /><span>Copy Walls from Previous</span>
            </button>
          )}
          <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
          {toolsActions.onUpdateMetadata && (
            <button style={menuBtnStyle} onClick={() => { toolsActions.onUpdateMetadata(); setOpen(false); }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <RotateCcw size={18} style={{ color: '#6b7280' }} /><span>Update Metadata</span>
            </button>
          )}
          {toolsActions.onRefresh && (
            <button style={menuBtnStyle} onClick={() => { toolsActions.onRefresh(); setOpen(false); }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <RefreshCw size={18} style={{ color: '#6b7280' }} /><span>Refresh</span>
            </button>
          )}
          {onDone && (
            <>
              <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
              <button style={menuBtnStyle} onClick={() => { onDone(); setOpen(false); }}
                onMouseOver={e => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="#10b981">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg><span>Done</span>
              </button>
            </>
          )}
          {onClose && !onDone && (
            <>
              <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
              <button style={menuBtnStyle} onClick={() => { onClose(); setOpen(false); }}
                onMouseOver={e => e.currentTarget.style.backgroundColor = '#fef2f2'}
                onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                <XCircle size={18} style={{ color: '#ef4444' }} /><span>Close Planner</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const TopDownWallPlanner = ({ walls, onUpdateWall, onClose, onAddWall, onAddPod, onAddEasel, easels = [], onUpdateEasel, floorData = null, structures = [], onAddStructure, onUpdateStructure,
  // Art Placement Mode props
  sceneData, setSceneData, inventoryData, collectionId, onPlaceArtwork, onPlaceArtworkOnEasel, onRepositionArtwork, onRemoveArtwork, onRefreshData, getOptimizedImageUrl,
  // Tools menu callbacks (from GalleryWallPlanner)
  toolsActions,
  // Delete callbacks
  onDeleteWall, onDeleteEasel, onDeleteStructure, onDeletePod
}) => {
  // Debug: Log what functions we received
  console.log('🔧 TopDownWallPlanner props:', {
    onUpdateWall: typeof onUpdateWall,
    onAddWall: typeof onAddWall,
    onClose: typeof onClose
  });
  console.log('🔧 onUpdateWall function:', onUpdateWall);
  const navigate = useNavigate();
  const handleDone = () => navigate(`/curator/collections/${collectionId}`);
  const [selectedWall, setSelectedWall] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const hasDraggedRef = useRef(false); // true only when mouse actually moved during a drag
  const [isDetailsPanelCollapsed, setIsDetailsPanelCollapsed] = useState(false);
  const [showAddWallModal, setShowAddWallModal] = useState(false);
  const [newWallData, setNewWallData] = useState({
    name: '',
    width: 120,
    height: 96,
    centerX: 0,
    centerZ: 0,
    rotationY: 0
  });
  
  // Pod creation state
  const [showAddPodModal, setShowAddPodModal] = useState(false);
  const [newPodData, setNewPodData] = useState({
    name: '',
    centerX: 0,
    centerZ: 0,
    rotationY: 0,
    wallWidth: 96,
    wallHeight: 96,
    tiltAngle: 70
  });
  
  // Easel creation state
  const [showAddEaselModal, setShowAddEaselModal] = useState(false);
  const [newEaselData, setNewEaselData] = useState({
    name: '',
    centerX: 0,
    centerZ: 0,
    rotationY: 0
  });
  
  // Structure creation state
  const [showAddStructureModal, setShowAddStructureModal] = useState(false);
  const [newStructureData, setNewStructureData] = useState({
    name: '',
    shape: 'column-square',
    width: 12,
    height: 96,
    depth: 12,
    centerX: 0,
    centerZ: 0,
    rotationY: 0,
    color: '#9ca3af'
  });

  // Dragging structure state
  const [isDraggingStructure, setIsDraggingStructure] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const selectedStructureRef = useRef(null);
  const [structureDragStart, setStructureDragStart] = useState({ x: 0, y: 0 });

  // Keep ref in sync with state so drag mouseup always has latest value
  useEffect(() => { selectedStructureRef.current = selectedStructure; }, [selectedStructure]);

  // Dragging easel state
  const [isDraggingEasel, setIsDraggingEasel] = useState(false);
  const [selectedEasel, setSelectedEasel] = useState(null);
  const [easelDragStart, setEaselDragStart] = useState({ x: 0, y: 0 });
  const easelDragRef = useRef(null); // tracks latest position during drag to avoid stale closure

  // Pod dragging state
  const [isDraggingPod, setIsDraggingPod] = useState(false);
  const [selectedPod, setSelectedPod] = useState(null); // podName string
  const [podRenameValue, setPodRenameValue] = useState(null);
  const [podDragStart, setPodDragStart] = useState({ x: 0, y: 0 });
  const [podDragDelta, setPodDragDelta] = useState({ x: 0, z: 0 }); // accumulated delta in inches
  // Floating overlay (D-pad) draggable position offset in pixels, relative to its anchor
  const [floatingOverlayOffset, setFloatingOverlayOffset] = useState({ x: 0, y: 0 });
  const floatingOverlayDragRef = useRef(null); // { startX, startY, baseX, baseY }
  // Reset overlay offset whenever the selected item changes
  useEffect(() => { setFloatingOverlayOffset({ x: 0, y: 0 }); }, [selectedPod, selectedWall?.element_id, selectedEasel?.element_id, selectedStructure?.element_id]);

  // Multi-select state (shift-click to add items, drag to move all together)
  const [multiSelected, setMultiSelected] = useState([]); // array of { type: 'wall'|'pod'|'easel'|'structure', id: string }
  const [isMultiDragging, setIsMultiDragging] = useState(false);
  const [multiDragStart, setMultiDragStart] = useState({ x: 0, y: 0 });
  const [multiDragDelta, setMultiDragDelta] = useState({ x: 0, z: 0 });
  const multiDragDeltaRef = useRef({ x: 0, z: 0 });
  
  // Art Placement Mode state
  const [placementMode, setPlacementMode] = useState(false);
  const [placementTarget, setPlacementTarget] = useState(null); // { type: 'wall'|'easel', element: wallObj|easelObj }
  const [inventorySearch, setInventorySearch] = useState('');
  const [isDraggingArtwork, setIsDraggingArtwork] = useState(false);
  const [draggingArtworkItem, setDraggingArtworkItem] = useState(null);
  const [selectedArtworkId, setSelectedArtworkId] = useState(null);
  
  // Inventory tabs: accepted / pending
  const [inventoryTab, setInventoryTab] = useState('accepted');
  
  // Layout inventory preview toggle
  const [showLayoutInventory, setShowLayoutInventory] = useState(false);
  
  // Easel Gallery strip toggle
  const [showEaselGallery, setShowEaselGallery] = useState(false);
  
  // Artist search state
  const [showArtistSearch, setShowArtistSearch] = useState(false);
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [artistSearchResults, setArtistSearchResults] = useState(null);
  const [isSearchingArtist, setIsSearchingArtist] = useState(false);
  const [addingArtworkId, setAddingArtworkId] = useState(null);
  const [artworkFilterQuery, setArtworkFilterQuery] = useState('');
  const [searchStatusMsg, setSearchStatusMsg] = useState(null);
  
  // Layout search (find placed artwork by artist/title)
  const [layoutSearchQuery, setLayoutSearchQuery] = useState('');
  const [layoutSearchOpen, setLayoutSearchOpen] = useState(false);
  const layoutSearchRef = useRef(null);
  const [searchHighlight, setSearchHighlight] = useState(null); // { type: 'wall'|'pod'|'easel', id: string }
  
  // Inline pod rename in art placement screen
  const [placementRenaming, setPlacementRenaming] = useState(false);
  const [placementRenameValue, setPlacementRenameValue] = useState('');
  
  // Identify pod groups from wall text_content metadata
  const podGroups = useMemo(() => {
    const groups = {};
    (walls || []).forEach(wall => {
      try {
        const meta = wall.text_content ? JSON.parse(wall.text_content) : null;
        if (meta?.pod_group) {
          if (!groups[meta.pod_group]) {
            groups[meta.pod_group] = { name: meta.pod_group, template: meta.template, walls: [] };
          }
          groups[meta.pod_group].walls.push(wall);
        }
      } catch { /* not a pod wall */ }
    });
    return groups;
  }, [walls]);

  // Identify easel groups from text_content metadata
  const easelGroups = useMemo(() => {
    const groups = {};
    (easels || []).forEach(easel => {
      try {
        const meta = easel.text_content ? JSON.parse(easel.text_content) : null;
        if (meta?.easel_group) {
          if (!groups[meta.easel_group]) {
            groups[meta.easel_group] = { name: meta.easel_group, easels: [] };
          }
          groups[meta.easel_group].easels.push(easel);
        }
      } catch { /* not a grouped easel */ }
    });
    return groups;
  }, [easels]);

  // Ungrouped easels (not assigned to any group)
  const ungroupedEasels = useMemo(() => {
    const grouped = new Set();
    Object.values(easelGroups).forEach(g => g.easels.forEach(e => grouped.add(e.element_id)));
    return (easels || []).filter(e => !grouped.has(e.element_id));
  }, [easels, easelGroups]);

  // Index of all placed artwork with location info (for layout search)
  const placedArtworkIndex = useMemo(() => {
    const items = [];
    // Walls (including pod walls)
    (sceneData?.walls || []).forEach(sw => {
      (sw.artwork || []).forEach(art => {
        // Find the wall_2d record for label/pod info
        const wallRec = (walls || []).find(w => String(w.element_id) === String(sw.element_id));
        let locationType = 'wall';
        let locationLabel = wallRec?.source || 'Wall';
        let podName = null;
        try {
          const meta = wallRec?.text_content ? JSON.parse(wallRec.text_content) : null;
          if (meta?.pod_group) {
            locationType = 'pod';
            locationLabel = meta.pod_group;
            podName = meta.pod_group;
          }
        } catch { /* ok */ }
        items.push({
          artwork: art,
          locationType,
          locationLabel,
          podName,
          wallObj: wallRec,
          easelObj: null,
        });
      });
    });
    // Easels
    (sceneData?.easels || []).forEach(se => {
      (se.artwork || []).forEach(art => {
        const easelRec = (easels || []).find(e => String(e.element_id) === String(se.element_id));
        items.push({
          artwork: art,
          locationType: 'easel',
          locationLabel: easelRec?.source || 'Easel',
          podName: null,
          wallObj: null,
          easelObj: easelRec,
        });
      });
    });
    return items;
  }, [sceneData, walls, easels]);

  // Filtered placed artwork based on layout search query
  const layoutSearchResults = useMemo(() => {
    if (!layoutSearchQuery.trim()) return [];
    const q = layoutSearchQuery.toLowerCase();
    return placedArtworkIndex.filter(item =>
      (item.artwork.title || '').toLowerCase().includes(q) ||
      (item.artwork.artist_name || '').toLowerCase().includes(q)
    );
  }, [placedArtworkIndex, layoutSearchQuery]);
  
  // Pan and zoom state
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [lastPanTime, setLastPanTime] = useState(0);
  const canvasRef = useRef(null);
  
  // Wall material presets similar to FloorManager
  const wallMaterialPresets = {
    drywall: {
      name: 'Drywall',
      color: '#F5F5F5',
      texture: '🏠',
      description: 'Standard painted drywall'
    },
    brick: {
      name: 'Brick',
      color: '#B22222',
      texture: '🧱',
      description: 'Exposed brick wall'
    },
    concrete: {
      name: 'Concrete',
      color: '#C0C0C0',
      texture: '🏗️',
      description: 'Raw concrete finish'
    },
    wood: {
      name: 'Wood',
      color: '#8B4513',
      texture: '🪵',
      description: 'Wood paneling or siding'
    },
    metal: {
      name: 'Metal',
      color: '#708090',
      texture: '⚙️',
      description: 'Metal or steel panel'
    },
    glass: {
      name: 'Glass',
      color: '#E6F3FF',
      texture: '🪟',
      description: 'Glass partition or window wall'
    },
    fabric: {
      name: 'Fabric',
      color: '#DDA0DD',
      texture: '🧵',
      description: 'Fabric-covered or acoustic panel'
    },
    stone: {
      name: 'Stone',
      color: '#808080',
      texture: '🪨',
      description: 'Natural stone or marble'
    }
  };

  // Handle wall material preset selection
  const handleWallMaterialChange = (materialType) => {
    const preset = wallMaterialPresets[materialType];
    if (!selectedWall) return;
    setSelectedWall({
      ...selectedWall,
      color: materialType,  // Store material type in color field
      background_color: preset.color  // Store actual color in background_color field
    });
  };

  // Responsive scaling based on screen size
  const getResponsiveScale = () => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    if (screenWidth < 1200) return 0.5;      // 1 inch = 0.5 pixels
    if (screenWidth < 1600) return 0.75;     // 1 inch = 0.75 pixels
    return 1;                                // 1 inch = 1 pixel (full scale)
  };
  
  const [baseScale, setBaseScale] = useState(getResponsiveScale());
  
  // Update base scale on window resize
  useEffect(() => {
    const handleResize = () => setBaseScale(getResponsiveScale());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-fit to screen on component load
  useEffect(() => {
    // Small delay to ensure component is fully rendered
    const timer = setTimeout(() => {
      fitToScreen();
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Global mouse move handler for wall dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDragging && selectedWall) {
        handleWallMouseMove(e);
      }
    };
    
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleWallMouseUp();
      }
    };
    
    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, selectedWall]);

  // Keyboard shortcuts for wall manipulation
  useEffect(() => {
    if (!selectedWall || placementMode) return;

    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const rotationAmount = e.shiftKey ? 1 : 15; // Fine vs coarse rotation
      const nudgeAmount = e.shiftKey ? 0.25 : 1;  // Fine (0.25") vs coarse (1") nudge
      
      switch (e.key) {
        case 'r':
        case 'R':
          e.preventDefault();
          console.log('🔄 Rotating wall clockwise');
          const newWall = {
            ...selectedWall,
            wall_rotation_y: (parseFloat(selectedWall.wall_rotation_y) || 0) + rotationAmount
          };
          setSelectedWall(newWall);
          break;
        case 'e':
        case 'E':
          e.preventDefault();
          console.log('🔄 Rotating wall counter-clockwise');
          const newWallCCW = {
            ...selectedWall,
            wall_rotation_y: (parseFloat(selectedWall.wall_rotation_y) || 0) - rotationAmount
          };
          setSelectedWall(newWallCCW);
          break;
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight': {
          e.preventDefault();
          let dx = 0, dz = 0;
          if (e.key === 'ArrowUp') dz = -nudgeAmount;
          if (e.key === 'ArrowDown') dz = nudgeAmount;
          if (e.key === 'ArrowLeft') dx = -nudgeAmount;
          if (e.key === 'ArrowRight') dx = nudgeAmount;
          let nudged = {
            ...selectedWall,
            wall_center_x: (parseFloat(selectedWall.wall_center_x) || 0) + dx,
            wall_center_z: (parseFloat(selectedWall.wall_center_z) || 0) + dz
          };
          // Try to snap to nearby endpoints after nudging
          if (!e.shiftKey) {
            nudged = snapWallToEndpoints(nudged);
          }
          setSelectedWall(nudged);
          onUpdateWall(nudged);
          break;
        }
        case 'Enter':
          e.preventDefault();
          console.log('🔄 Saving wall position after keyboard changes');
          onUpdateWall(selectedWall);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [selectedWall, onUpdateWall, placementMode]);
  
  // Calculate dynamic gallery dimensions based on actual wall positions
  const calculateGalleryDimensions = () => {
    if (!walls || walls.length === 0) {
      return { width: 1200, depth: 800 }; // Default fallback
    }
    
    let minX = Infinity, maxX = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    
    walls.forEach(wall => {
      const centerX = parseFloat(wall.wall_center_x) || 0;
      const centerZ = parseFloat(wall.wall_center_z) || 0;
      const width = parseFloat(wall.width) || 0;
      const depth = parseFloat(wall.depth) || 2;
      
      // Calculate wall boundaries
      const wallMinX = centerX - width / 2;
      const wallMaxX = centerX + width / 2;
      const wallMinZ = centerZ - depth / 2;
      const wallMaxZ = centerZ + depth / 2;
      
      minX = Math.min(minX, wallMinX);
      maxX = Math.max(maxX, wallMaxX);
      minZ = Math.min(minZ, wallMinZ);
      maxZ = Math.max(maxZ, wallMaxZ);
    });
    
    // Add padding around the walls
    const padding = 200; // 200 inches of padding
    const calculatedWidth = Math.max(1200, maxX - minX + padding);
    const calculatedDepth = Math.max(800, maxZ - minZ + padding);
    
    console.log('Calculated gallery dimensions:', {
      minX, maxX, minZ, maxZ,
      width: calculatedWidth,
      depth: calculatedDepth
    });
    
    return { width: calculatedWidth, depth: calculatedDepth };
  };
  
  const { width: galleryWidth, depth: galleryDepth } = calculateGalleryDimensions();
  
  // Final scale combines base scale and zoom level
  const finalScale = baseScale * zoomLevel;
  
  // Convert inches to pixels
  const inchesToPixels = (inches) => inches * finalScale;
  
  // Convert gallery coordinates to canvas coordinates (with pan and zoom)
  const galleryToCanvas = (x, z) => ({
    x: (x + galleryWidth / 2) * finalScale + panOffset.x,
    y: (z + galleryDepth / 2) * finalScale + panOffset.y
  });
  
  // Convert canvas coordinates to gallery coordinates (accounting for pan and zoom)
  const canvasToGallery = (canvasX, canvasY) => ({
    x: ((canvasX - panOffset.x) / finalScale) - galleryWidth / 2,
    z: ((canvasY - panOffset.y) / finalScale) - galleryDepth / 2
  });
  
  // Pan functionality
  const handlePanStart = (e) => {
    if (e.button !== 0 || isDragging) return; // Only left mouse button, not when dragging walls
    setIsPanning(true);
    setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handlePanMove = (e) => {
    if (!isPanning || isDragging) return;
    setPanOffset({
      x: e.clientX - panStart.x,
      y: e.clientY - panStart.y
    });
    e.preventDefault();
  };
  
  const handlePanEnd = (e) => {
    if (isPanning) {
      setIsPanning(false);
      setLastPanTime(Date.now());
    }
  };
  
  // Zoom functionality
  const handleZoom = (delta) => {
    const newZoom = Math.max(0.1, Math.min(5, zoomLevel + delta));
    setZoomLevel(newZoom);
  };
  

  
  const resetView = () => {
    setPanOffset({ x: 0, y: 0 });
    setZoomLevel(1);
  };
  
  const fitToScreen = () => {
    if (!canvasRef.current) return;
    
    const canvasRect = canvasRef.current.getBoundingClientRect();
    const availableWidth = canvasRect.width - 40; // Account for margins
    const availableHeight = canvasRect.height - 40;
    
    const scaleX = availableWidth / galleryWidth;
    const scaleY = availableHeight / galleryDepth;
    const newScale = Math.min(scaleX, scaleY, 1); // Don't zoom in beyond 1:1
    
    setZoomLevel(newScale / baseScale);
    setPanOffset({ x: 0, y: 0 });
  };
  
  const handleWallClick = (wall) => {
    setSelectedWall(wall);
    setSelectedPod(null);
    setSelectedEasel(null);
    setSelectedStructure(null);
    if (placementMode) {
      setPlacementTarget({ type: 'wall', element: wall });
    }
  };
  
  // Wall dragging functionality
  const handleWallMouseDown = (e, wall) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Set dragging state immediately
    hasDraggedRef.current = false;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setSelectedWall(wall);
    setSelectedPod(null);
    setSelectedEasel(null);
    setSelectedStructure(null);
    
    console.log('🔄 Wall drag started:', wall.source);
    console.log('🔄 Mouse position:', { x: e.clientX, y: e.clientY });
    console.log('🔄 Wall position:', { x: wall.wall_center_x, z: wall.wall_center_z });
  };
  
  // Get wall endpoints (the two ends of a wall line based on center + rotation)
  const getWallEndpoints = (wall) => {
    const cx = parseFloat(wall.wall_center_x) || 0;
    const cz = parseFloat(wall.wall_center_z) || 0;
    const hw = (parseFloat(wall.width) || 0) / 2;
    const rad = ((parseFloat(wall.wall_rotation_y) || 0) * Math.PI) / 180;
    const cosR = Math.cos(rad);
    const sinR = Math.sin(rad);
    return [
      { x: cx - hw * cosR, z: cz - hw * sinR },
      { x: cx + hw * cosR, z: cz + hw * sinR }
    ];
  };

  // Snap wall endpoints to nearby endpoints of other walls
  const SNAP_THRESHOLD = 12; // 12 inches
  const snapWallToEndpoints = (wall) => {
    const otherWalls = (walls || []).filter(w => w.element_id !== wall.element_id);
    if (otherWalls.length === 0) return wall;
    // Collect all endpoint targets
    const targets = [];
    otherWalls.forEach(w => getWallEndpoints(w).forEach(pt => targets.push(pt)));
    // Also include structure endpoints as snap targets
    (structures || []).forEach(s => getStructureEndpoints(s).forEach(pt => targets.push(pt)));
    // Get endpoints of this wall
    const myEndpoints = getWallEndpoints(wall);
    let bestDist = SNAP_THRESHOLD;
    let bestShift = null;
    myEndpoints.forEach(myPt => {
      targets.forEach(tPt => {
        const dx = tPt.x - myPt.x;
        const dz = tPt.z - myPt.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < bestDist) {
          bestDist = dist;
          bestShift = { x: dx, z: dz };
        }
      });
    });
    if (bestShift) {
      return {
        ...wall,
        wall_center_x: (parseFloat(wall.wall_center_x) || 0) + bestShift.x,
        wall_center_z: (parseFloat(wall.wall_center_z) || 0) + bestShift.z
      };
    }
    return wall;
  };

  // Get structure endpoints (corners/edges) for snapping
  const getStructureEndpoints = (s) => {
    const cx = parseFloat(s.position_x) || 0;
    const cz = parseFloat(s.position_z) || 0;
    const hw = (parseFloat(s.width) || 12) / 2;
    const hd = (parseFloat(s.depth) || 12) / 2;
    const rad = ((parseFloat(s.rotation_y) || 0) * Math.PI) / 180;
    const cosR = Math.cos(rad);
    const sinR = Math.sin(rad);
    // Return 4 corners of the footprint
    return [
      { x: cx + (-hw * cosR - (-hd) * sinR), z: cz + (-hw * sinR + (-hd) * cosR) },
      { x: cx + ( hw * cosR - (-hd) * sinR), z: cz + ( hw * sinR + (-hd) * cosR) },
      { x: cx + ( hw * cosR - ( hd) * sinR), z: cz + ( hw * sinR + ( hd) * cosR) },
      { x: cx + (-hw * cosR - ( hd) * sinR), z: cz + (-hw * sinR + ( hd) * cosR) }
    ];
  };

  // Snap structure to nearby wall endpoints and other structure endpoints
  const snapStructureToEndpoints = (structure) => {
    // Collect all snap targets: wall endpoints + other structure endpoints
    const targets = [];
    (walls || []).forEach(w => getWallEndpoints(w).forEach(pt => targets.push(pt)));
    (structures || []).filter(s => s.element_id !== structure.element_id)
      .forEach(s => getStructureEndpoints(s).forEach(pt => targets.push(pt)));
    if (targets.length === 0) return structure;

    const myEndpoints = getStructureEndpoints(structure);
    let bestDist = SNAP_THRESHOLD;
    let bestShift = null;
    myEndpoints.forEach(myPt => {
      targets.forEach(tPt => {
        const dx = tPt.x - myPt.x;
        const dz = tPt.z - myPt.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < bestDist) {
          bestDist = dist;
          bestShift = { x: dx, z: dz };
        }
      });
    });
    if (bestShift) {
      return {
        ...structure,
        position_x: (parseFloat(structure.position_x) || 0) + bestShift.x,
        position_z: (parseFloat(structure.position_z) || 0) + bestShift.z
      };
    }
    return structure;
  };

  const handleWallMouseMove = (e) => {
    if (!isDragging || !selectedWall) return;
    
    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) hasDraggedRef.current = true;
    
    // Convert pixel deltas to inch deltas (accounting for zoom)
    const deltaXInches = deltaX / finalScale;
    const deltaZInches = deltaY / finalScale;
    
    // Update wall position locally (don't save to database yet)
    let newWall = {
      ...selectedWall,
      wall_center_x: (parseFloat(selectedWall.wall_center_x) || 0) + deltaXInches,
      wall_center_z: (parseFloat(selectedWall.wall_center_z) || 0) + deltaZInches
    };

    // Snap to nearby wall endpoints
    newWall = snapWallToEndpoints(newWall);
    
    // Update local state for visual feedback
    setSelectedWall(newWall);
    
    // Update the wall in the walls array for immediate visual update
    const updatedWalls = walls.map(wall => 
      wall.element_id === selectedWall.element_id ? newWall : wall
    );
    
    // Update drag start position
    setDragStart({ x: e.clientX, y: e.clientY });
  };
  
  const handleWallMouseUp = () => {
    if (isDragging && selectedWall) {
      if (hasDraggedRef.current) {
        console.log('🔄 Drag ended, saving final wall position:', selectedWall);
        onUpdateWall(selectedWall);
      }
      setIsDragging(false);
      hasDraggedRef.current = false;
    }
  };
  
  const handleSavePosition = async (wall) => {
    try {
      console.log('🔄 Saving wall position:', wall);
      console.log('🔄 Wall element_id:', wall.element_id);
      
      // Validate wall data
      if (!wall.element_id) {
        throw new Error('Wall element_id is missing');
      }
      
      // Ensure all required fields have valid values
      const wallDataToSave = {
        ...wall,
        wall_center_x: parseFloat(wall.wall_center_x) || 0,
        wall_center_y: parseFloat(wall.wall_center_y) || 72,
        wall_center_z: parseFloat(wall.wall_center_z) || 0,
        wall_rotation_y: parseFloat(wall.wall_rotation_y) || 0,
        display_order: parseInt(wall.display_order) || 0
      };
      
      console.log('🔄 Wall data being sent:', {
        source: wallDataToSave.source,
        width: wallDataToSave.width,
        height: wallDataToSave.height,
        wall_center_x: wallDataToSave.wall_center_x,
        wall_center_y: wallDataToSave.wall_center_y,
        wall_center_z: wallDataToSave.wall_center_z,
        wall_rotation_y: wallDataToSave.wall_rotation_y,
        display_order: wallDataToSave.display_order,
        color: wallDataToSave.color,
        background_color: wallDataToSave.background_color
      });
      
      console.log('🔄 About to call onUpdateWall with function:', onUpdateWall);
      console.log('🔄 Function name:', onUpdateWall.name);
      console.log('🔄 Function toString:', onUpdateWall.toString().substring(0, 100));
      
      const result = await onUpdateWall(wallDataToSave);
      console.log('✅ Save result:', result);
      
      if (result && result.success) {
        console.log('✅ Save successful');
        // Also update the wall in the walls array
        const updatedWalls = walls.map(w => 
          w.element_id === wall.element_id ? wallDataToSave : w
        );
        // This will trigger a re-render with the updated data
      } else {
        console.warn('⚠️ Save result indicates failure:', result);
        alert('Save may have failed. Check console for details.');
      }
    } catch (error) {
      console.error('❌ Error updating wall position:', error);
      alert(`Error saving wall: ${error.message}`);
    }
  };
  
  const handleCancelEdit = () => {
    if (!selectedWall) return;
    const currentWall = walls.find(w => w.element_id === selectedWall.element_id);
    if (currentWall) {
      setSelectedWall(currentWall);
    }
  };
  
  // Refresh editing wall data to match current wall state
  const refreshEditingWall = () => {
    if (selectedWall) {
      const currentWall = walls.find(w => w.element_id === selectedWall.element_id);
      if (currentWall) {
        console.log('🔄 Refreshing editing wall data:', currentWall);
        setSelectedWall(currentWall);
      }
    }
  };
  
  const handleAddWall = async () => {
    try {
      const wallData = {
        name: newWallData.name,
        width: newWallData.width,
        height: newWallData.height,
        wall_center_x: newWallData.centerX,
        wall_center_y: 72, // Standard height
        wall_center_z: newWallData.centerZ,
        wall_rotation_y: newWallData.rotationY
      };
      
      await onAddWall(wallData);
      setShowAddWallModal(false);
      setNewWallData({
        name: '',
        width: 120,
        height: 96,
        centerX: 0,
        centerZ: 0,
        rotationY: 0
      });
    } catch (error) {
      console.error('Error adding wall:', error);
    }
  };
  
  const handleAddPod = async () => {
    try {
      if (!onAddPod) {
        console.warn('onAddPod not provided');
        return;
      }
      const trimmedName = newPodData.name.trim();
      if (!trimmedName) {
        alert('Please enter a pod name.');
        return;
      }
      if (podGroups[trimmedName]) {
        alert(`A pod named "${trimmedName}" already exists. Please choose a different name.`);
        return;
      }
      await onAddPod({
        name: newPodData.name,
        centerX: newPodData.centerX,
        centerZ: newPodData.centerZ,
        rotationY: newPodData.rotationY,
        wallWidth: newPodData.wallWidth,
        wallHeight: newPodData.wallHeight,
        tiltAngle: newPodData.tiltAngle
      });
      setShowAddPodModal(false);
      setNewPodData({ name: '', centerX: 0, centerZ: 0, rotationY: 0, wallWidth: 96, wallHeight: 96, tiltAngle: 70 });
    } catch (error) {
      console.error('Error adding pod:', error);
    }
  };
  
  const handleAddEasel = async () => {
    try {
      if (!onAddEasel) {
        console.warn('onAddEasel not provided');
        return;
      }
      await onAddEasel({
        name: newEaselData.name,
        centerX: newEaselData.centerX,
        centerZ: newEaselData.centerZ,
        rotationY: newEaselData.rotationY
      });
      setShowAddEaselModal(false);
      setNewEaselData({ name: '', centerX: 0, centerZ: 0, rotationY: 0 });
    } catch (error) {
      console.error('Error adding easel:', error);
    }
  };

  const handleAddStructure = async () => {
    try {
      if (!onAddStructure) {
        console.warn('onAddStructure not provided');
        return;
      }
      await onAddStructure({
        name: newStructureData.name,
        shape: newStructureData.shape,
        width: newStructureData.width,
        height: newStructureData.height,
        depth: newStructureData.depth,
        centerX: newStructureData.centerX,
        centerZ: newStructureData.centerZ,
        rotationY: newStructureData.rotationY,
        color: newStructureData.color
      });
      setShowAddStructureModal(false);
      setNewStructureData({ name: '', shape: 'column-square', width: 12, height: 96, depth: 12, centerX: 0, centerZ: 0, rotationY: 0, color: '#9ca3af' });
    } catch (error) {
      console.error('Error adding structure:', error);
    }
  };

  // Duplicate selected items — offset by item width to the right
  const handleDuplicate = async () => {
    try {
      const itemsToDuplicate = multiSelected.length > 0
        ? multiSelected
        : selectedWall ? [{ type: 'wall', id: selectedWall.element_id }]
        : selectedPod ? [{ type: 'pod', id: selectedPod }]
        : selectedEasel ? [{ type: 'easel', id: selectedEasel.element_id }]
        : selectedStructure ? [{ type: 'structure', id: selectedStructure.element_id }]
        : [];

      if (itemsToDuplicate.length === 0) return;

      for (const item of itemsToDuplicate) {
        if (item.type === 'wall') {
          const wall = (walls || []).find(w => w.element_id === item.id);
          if (!wall || !onAddWall) continue;
          const w = parseFloat(wall.width) || 120;
          const offset = w + 12; // wall width + 12" gap
          const rotRad = ((parseFloat(wall.wall_rotation_y) || 0) * Math.PI) / 180;
          // Offset perpendicular to the wall's facing direction (along its length)
          const ox = offset * Math.cos(rotRad);
          const oz = offset * -Math.sin(rotRad);
          await onAddWall({
            name: (wall.source || 'Wall') + ' copy',
            width: w,
            height: parseFloat(wall.height) || 96,
            wall_center_x: (parseFloat(wall.wall_center_x) || 0) + ox,
            wall_center_y: parseFloat(wall.wall_center_y) || 72,
            wall_center_z: (parseFloat(wall.wall_center_z) || 0) + oz,
            wall_rotation_y: parseFloat(wall.wall_rotation_y) || 0
          });
        } else if (item.type === 'pod') {
          const podGroup = podGroups[item.id];
          if (!podGroup || !onAddPod) continue;
          const bbox = getPodBoundingBox(podGroup.walls);
          const offset = bbox.width + 12; // pod width + 12" gap
          const firstWall = podGroup.walls[0];
          let meta;
          try { meta = firstWall.text_content ? JSON.parse(firstWall.text_content) : {}; } catch { meta = {}; }
          const existingNames = Object.keys(podGroups);
          let copyNum = 1;
          let newName = `${item.id} copy`;
          while (existingNames.includes(newName)) {
            copyNum++;
            newName = `${item.id} copy ${copyNum}`;
          }
          await onAddPod({
            name: newName,
            centerX: bbox.centerX + offset,
            centerZ: bbox.centerZ,
            rotationY: 0,
            wallWidth: meta.wallWidth || 96,
            wallHeight: meta.wallHeight || 96,
            tiltAngle: meta.tilt_angle || 70
          });
        } else if (item.type === 'easel') {
          const easel = (easels || []).find(e => e.element_id === item.id);
          if (!easel || !onAddEasel) continue;
          await onAddEasel({
            name: (easel.source || 'Easel') + ' copy',
            centerX: (parseFloat(easel.position_x) || 0) + 36, // 3ft offset
            centerZ: parseFloat(easel.position_z) || 0,
            rotationY: parseFloat(easel.rotation_y) || 0
          });
        } else if (item.type === 'structure') {
          const s = (structures || []).find(st => st.element_id === item.id);
          if (!s || !onAddStructure) continue;
          let meta;
          try { meta = s.text_content ? JSON.parse(s.text_content) : {}; } catch { meta = {}; }
          const w = parseFloat(s.width) || 12;
          await onAddStructure({
            name: (s.source || 'Structure') + ' copy',
            shape: meta.shape || 'column-square',
            width: w,
            height: parseFloat(s.height) || 96,
            depth: parseFloat(s.depth) || 12,
            centerX: (parseFloat(s.position_x) || 0) + w + 12,
            centerZ: parseFloat(s.position_z) || 0,
            rotationY: parseFloat(s.rotation_y) || 0,
            color: s.color || '#9ca3af'
          });
        }
      }
      console.log(`📋 Duplicated ${itemsToDuplicate.length} item(s)`);
    } catch (error) {
      console.error('Error duplicating:', error);
    }
  };

  // Easel dragging
  // Pod drag handlers
  const handlePodMouseDown = (e, podName) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDraggingPod(true);
    hasDraggedRef.current = false;
    setPodDragStart({ x: e.clientX, y: e.clientY });
    setSelectedPod(podName);
    setPodDragDelta({ x: 0, z: 0 });
    setSelectedWall(null);
    setSelectedEasel(null);
    setSelectedStructure(null);
  };

  useEffect(() => {
    if (!isDraggingPod || !selectedPod) return;
    const handleMove = (e) => {
      const deltaX = e.clientX - podDragStart.x;
      const deltaY = e.clientY - podDragStart.y;
      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) hasDraggedRef.current = true;
      const deltaXInches = deltaX / finalScale;
      const deltaZInches = deltaY / finalScale;
      setPodDragDelta(prev => ({
        x: prev.x + deltaXInches,
        z: prev.z + deltaZInches
      }));
      setPodDragStart({ x: e.clientX, y: e.clientY });
    };
    const handleUp = () => {
      // Apply delta to all walls in the pod, then snap the group
      const podGroup = podGroups[selectedPod];
      if (podGroup && onUpdateWall) {
        // First compute moved walls
        let movedWalls = podGroup.walls.map(wall => ({
          ...wall,
          wall_center_x: (parseFloat(wall.wall_center_x) || 0) + podDragDelta.x,
          wall_center_z: (parseFloat(wall.wall_center_z) || 0) + podDragDelta.z
        }));
        // Snap: find closest endpoint of any pod wall to any non-pod wall endpoint
        const nonPodWalls = (walls || []).filter(w => {
          try {
            const meta = w.text_content ? JSON.parse(w.text_content) : null;
            return !meta?.pod_group || meta.pod_group !== selectedPod;
          } catch { return true; }
        });
        const targets = [];
        nonPodWalls.forEach(w => getWallEndpoints(w).forEach(pt => targets.push(pt)));
        if (targets.length > 0) {
          let bestDist = SNAP_THRESHOLD;
          let bestShift = null;
          movedWalls.forEach(mw => {
            getWallEndpoints(mw).forEach(myPt => {
              targets.forEach(tPt => {
                const dx = tPt.x - myPt.x;
                const dz = tPt.z - myPt.z;
                const dist = Math.sqrt(dx * dx + dz * dz);
                if (dist < bestDist) {
                  bestDist = dist;
                  bestShift = { x: dx, z: dz };
                }
              });
            });
          });
          if (bestShift) {
            movedWalls = movedWalls.map(w => ({
              ...w,
              wall_center_x: (parseFloat(w.wall_center_x) || 0) + bestShift.x,
              wall_center_z: (parseFloat(w.wall_center_z) || 0) + bestShift.z
            }));
          }
        }
        if (hasDraggedRef.current) {
          movedWalls.forEach(w => onUpdateWall(w));
        }
      }
      setIsDraggingPod(false);
      setPodDragDelta({ x: 0, z: 0 });
      hasDraggedRef.current = false;
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isDraggingPod, selectedPod, podDragStart, podDragDelta, finalScale, podGroups, onUpdateWall]);

  // Rotate entire pod around its center
  const handleRotatePod = (podName, angleDeg) => {
    const podGroup = podGroups[podName];
    if (!podGroup || !onUpdateWall) return;
    const bbox = getPodBoundingBox(podGroup.walls);
    const cx = bbox.centerX;
    const cz = bbox.centerZ;
    const rad = (angleDeg * Math.PI) / 180;
    const cosA = Math.cos(rad);
    const sinA = Math.sin(rad);
    podGroup.walls.forEach(wall => {
      const wx = (parseFloat(wall.wall_center_x) || 0) - cx;
      const wz = (parseFloat(wall.wall_center_z) || 0) - cz;
      // Use A-Frame's Y-rotation matrix: x' = x*cos + z*sin, z' = -x*sin + z*cos
      const newX = wx * cosA + wz * sinA + cx;
      const newZ = -wx * sinA + wz * cosA + cz;
      const newRot = ((parseFloat(wall.wall_rotation_y) || 0) + angleDeg) % 360;
      onUpdateWall({ ...wall, wall_center_x: newX, wall_center_z: newZ, wall_rotation_y: newRot });
    });
  };

  // Translate all walls in a pod by a delta in inches (X/Z) or set Y center.
  const handleMovePod = (podName, { dx = 0, dz = 0, dy = null } = {}) => {
    const podGroup = podGroups[podName];
    if (!podGroup || !onUpdateWall) return;
    podGroup.walls.forEach(wall => {
      const updates = { ...wall };
      if (dx) updates.wall_center_x = (parseFloat(wall.wall_center_x) || 0) + dx;
      if (dz) updates.wall_center_z = (parseFloat(wall.wall_center_z) || 0) + dz;
      if (dy !== null) updates.wall_center_y = dy;
      onUpdateWall(updates);
    });
  };

  // Set absolute pod center (X or Z) by translating all walls by the delta needed.
  const handleSetPodCenter = (podName, axis, value) => {
    const podGroup = podGroups[podName];
    if (!podGroup) return;
    const bbox = getPodBoundingBox(podGroup.walls);
    const target = parseFloat(value);
    if (isNaN(target)) return;
    if (axis === 'x') handleMovePod(podName, { dx: target - bbox.centerX });
    else if (axis === 'z') handleMovePod(podName, { dz: target - bbox.centerZ });
  };

  const handleEaselMouseDown = (e, easel) => {
    e.stopPropagation();
    e.preventDefault();
    hasDraggedRef.current = false;
    setIsDraggingEasel(true);
    setEaselDragStart({ x: e.clientX, y: e.clientY });
    setSelectedEasel(easel);
    easelDragRef.current = easel;
    setSelectedWall(null);
    setSelectedPod(null);
    setSelectedStructure(null);
  };

  useEffect(() => {
    if (!isDraggingEasel || !selectedEasel) return;
    const handleMove = (e) => {
      const deltaX = e.clientX - easelDragStart.x;
      const deltaY = e.clientY - easelDragStart.y;
      const deltaXInches = deltaX / finalScale;
      const deltaZInches = deltaY / finalScale;
      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) hasDraggedRef.current = true;
      setSelectedEasel(prev => {
        const updated = {
          ...prev,
          position_x: (parseFloat(prev.position_x) || 0) + deltaXInches,
          position_z: (parseFloat(prev.position_z) || 0) + deltaZInches
        };
        easelDragRef.current = updated;
        return updated;
      });
      setEaselDragStart({ x: e.clientX, y: e.clientY });
    };
    const handleUp = () => {
      const latestEasel = easelDragRef.current;
      if (hasDraggedRef.current && latestEasel && onUpdateEasel) {
        onUpdateEasel(latestEasel);
      }
      setIsDraggingEasel(false);
      easelDragRef.current = null;
      hasDraggedRef.current = false;
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isDraggingEasel, selectedEasel, easelDragStart, finalScale]);

  // Structure dragging
  useEffect(() => {
    if (!isDraggingStructure || !selectedStructure) return;
    const handleMove = (e) => {
      const deltaX = e.clientX - structureDragStart.x;
      const deltaY = e.clientY - structureDragStart.y;
      const deltaXInches = deltaX / finalScale;
      const deltaZInches = deltaY / finalScale;
      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) hasDraggedRef.current = true;
      let updated = {
        ...selectedStructure,
        position_x: (parseFloat(selectedStructure.position_x) || 0) + deltaXInches,
        position_z: (parseFloat(selectedStructure.position_z) || 0) + deltaZInches
      };
      updated = snapStructureToEndpoints(updated);
      setSelectedStructure(updated);
      setStructureDragStart({ x: e.clientX, y: e.clientY });
    };
    const handleUp = () => {
      if (hasDraggedRef.current && selectedStructureRef.current && onUpdateStructure) {
        onUpdateStructure(selectedStructureRef.current);
      }
      setIsDraggingStructure(false);
      hasDraggedRef.current = false;
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isDraggingStructure, selectedStructure, structureDragStart, finalScale]);

  // Multi-select helpers
  const isMultiSelectedItem = (type, id) => multiSelected.some(m => m.type === type && m.id === id);

  const toggleMultiSelect = (type, id) => {
    setMultiSelected(prev => {
      const exists = prev.find(m => m.type === type && m.id === id);
      if (exists) return prev.filter(m => !(m.type === type && m.id === id));
      return [...prev, { type, id }];
    });
  };

  const clearMultiSelect = () => setMultiSelected([]);

  // Multi-drag: when dragging any multi-selected item, all items move together
  const handleMultiDragStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    hasDraggedRef.current = false;
    setIsMultiDragging(true);
    setMultiDragStart({ x: e.clientX, y: e.clientY });
    setMultiDragDelta({ x: 0, z: 0 });
    multiDragDeltaRef.current = { x: 0, z: 0 };
  };

  useEffect(() => {
    if (!isMultiDragging) return;
    const handleMove = (e) => {
      const dx = e.clientX - multiDragStart.x;
      const dy = e.clientY - multiDragStart.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) hasDraggedRef.current = true;
      const dxInches = dx / finalScale;
      const dzInches = dy / finalScale;
      setMultiDragDelta(prev => {
        const next = { x: prev.x + dxInches, z: prev.z + dzInches };
        multiDragDeltaRef.current = next;
        return next;
      });
      setMultiDragStart({ x: e.clientX, y: e.clientY });
    };
    const handleUp = () => {
      if (hasDraggedRef.current) {
        const delta = multiDragDeltaRef.current;
        // Apply accumulated delta to all selected items
        multiSelected.forEach(item => {
        if (item.type === 'wall') {
          const wall = (walls || []).find(w => w.element_id === item.id);
          if (wall && onUpdateWall) {
            onUpdateWall({
              ...wall,
              wall_center_x: (parseFloat(wall.wall_center_x) || 0) + delta.x,
              wall_center_z: (parseFloat(wall.wall_center_z) || 0) + delta.z
            });
          }
        } else if (item.type === 'pod') {
          const podGroup = podGroups[item.id];
          if (podGroup && onUpdateWall) {
            podGroup.walls.forEach(wall => {
              onUpdateWall({
                ...wall,
                wall_center_x: (parseFloat(wall.wall_center_x) || 0) + delta.x,
                wall_center_z: (parseFloat(wall.wall_center_z) || 0) + delta.z
              });
            });
          }
        } else if (item.type === 'easel') {
          const easel = (easels || []).find(e => e.element_id === item.id);
          if (easel && onUpdateEasel) {
            onUpdateEasel({
              ...easel,
              position_x: (parseFloat(easel.position_x) || 0) + delta.x,
              position_z: (parseFloat(easel.position_z) || 0) + delta.z
            });
          }
        } else if (item.type === 'structure') {
          const s = (structures || []).find(s => s.element_id === item.id);
          if (s && onUpdateStructure) {
            onUpdateStructure({
              ...s,
              position_x: (parseFloat(s.position_x) || 0) + delta.x,
              position_z: (parseFloat(s.position_z) || 0) + delta.z
            });
          }
        }
      });
      }
      setIsMultiDragging(false);
      setMultiDragDelta({ x: 0, z: 0 });
      multiDragDeltaRef.current = { x: 0, z: 0 };
      hasDraggedRef.current = false;
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [isMultiDragging, multiDragStart, multiSelected, finalScale, walls, easels, structures, podGroups, onUpdateWall, onUpdateEasel, onUpdateStructure]);

  // Helper: get pod bounding box for visual grouping
  const getPodBoundingBox = (podWalls) => {
    // Compute bounding box encompassing ALL walls in the pod
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;

    podWalls.forEach(wall => {
      const cx = parseFloat(wall.wall_center_x) || 0;
      const cz = parseFloat(wall.wall_center_z) || 0;
      const width = parseFloat(wall.width) || 0;
      let tiltDeg = 70;
      try {
        const meta = wall.text_content ? JSON.parse(wall.text_content) : null;
        tiltDeg = meta?.tilt_angle || 70;
      } catch { /* ignore */ }
      const tiltRad = (tiltDeg * Math.PI) / 180;
      const depth = (parseFloat(wall.height) || 96) * Math.cos(tiltRad);
      const rotRad = ((parseFloat(wall.wall_rotation_y) || 0) * Math.PI) / 180;

      const hw = width / 2;
      const hd = depth / 2;
      [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]].forEach(([lx, lz]) => {
        const rx = lx * Math.cos(rotRad) - lz * Math.sin(rotRad) + cx;
        const rz = lx * Math.sin(rotRad) + lz * Math.cos(rotRad) + cz;
        minX = Math.min(minX, rx);
        maxX = Math.max(maxX, rx);
        minZ = Math.min(minZ, rz);
        maxZ = Math.max(maxZ, rz);
      });
    });

    const pad = 6; // 6" padding
    return {
      minX: minX - pad, maxX: maxX + pad,
      minZ: minZ - pad, maxZ: maxZ + pad,
      centerX: (minX + maxX) / 2, centerZ: (minZ + maxZ) / 2,
      width: maxX - minX + pad * 2, depth: maxZ - minZ + pad * 2
    };
  };

  const handleCanvasClick = (e) => {
    // Only handle clicks if not clicking on a wall, not panning, not dragging, and enough time has passed since last pan
    const timeSinceLastPan = Date.now() - lastPanTime;
    const minTimeAfterPan = 300; // 300ms delay after panning
    
    if (e.target.classList.contains('gallery-canvas') && 
        !isPanning && 
        !isDragging && 
        timeSinceLastPan > minTimeAfterPan) {
      
      // If a wall is selected, deselect it first
      if (selectedWall || selectedEasel || selectedStructure || selectedPod || multiSelected.length > 0) {
        console.log('🔄 Deselecting by clicking white space');
        setSelectedWall(null);
        setSelectedEasel(null);
        setSelectedStructure(null);
        setSelectedPod(null);
        setPodRenameValue(null);
        clearMultiSelect();
        return;
      }
      
      // If no wall is selected, then allow adding a new wall
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Convert to gallery coordinates
      const galleryCoords = canvasToGallery(x, y);
      
      // Set default position for new wall
      setNewWallData(prev => ({
        ...prev,
        centerX: galleryCoords.x,
        centerZ: galleryCoords.y
      }));
      
      setShowAddWallModal(true);
    }
  };
  
  const getWallDisplayInfo = (wall) => {
    const centerX = parseFloat(wall.wall_center_x) || 0;
    const centerZ = parseFloat(wall.wall_center_z) || 0;
    const rotationY = parseFloat(wall.wall_rotation_y) || 0;
    const width = parseFloat(wall.width) || 0;
    const depth = parseFloat(wall.depth) || 2; // Default 2 inch depth
    
    // Debug: Log rotation values for troubleshooting
    if (wall.source && wall.source.includes('Angled')) {
      console.log('🔄 Rotation debug for', wall.source, ':', {
        original: rotationY,
        negated: -rotationY,
        '3D_view_should_match': rotationY,
        '2D_view_should_match': -rotationY
      });
    }
    
    // Convert to canvas coordinates
    const canvasPos = galleryToCanvas(centerX, centerZ);
    
    // Calculate wall corners based on rotation
    const halfWidth = width / 2;
    const halfDepth = depth / 2;
    
    // Rotate the wall rectangle
    const angle = (rotationY * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    
    const corners = [
      { x: -halfWidth, z: -halfDepth },
      { x: halfWidth, z: -halfDepth },
      { x: halfWidth, z: halfDepth },
      { x: -halfWidth, z: halfDepth }
    ].map(corner => ({
      x: corner.x * cos - corner.z * sin,
      z: corner.x * sin + corner.z * cos
    }));
    
    // Convert corners to canvas coordinates
    const canvasCorners = corners.map(corner => ({
      x: canvasPos.x + corner.x * finalScale,
      y: canvasPos.y + corner.z * finalScale
    }));
    
    return {
      canvasPos,
      canvasCorners,
      centerX,
      centerZ,
      rotationY: -rotationY, // Negate rotation to match 3D view convention
      width,
      depth
    };
  };

  // ─── Art Placement Mode helpers ───────────────────────────────────
  const placementAvailable = !!(sceneData && inventoryData && collectionId);

  // Set of wall element_ids that have artwork placed on them
  const wallsWithArtwork = useMemo(() => {
    const s = new Set();
    (sceneData?.walls || []).forEach(w => { if (w.artwork?.length > 0) s.add(w.element_id); });
    return s;
  }, [sceneData]);

  // Set of easel element_ids that have artwork
  const easelsWithArtwork = useMemo(() => {
    const s = new Set();
    (sceneData?.easels || []).forEach(e => { if (e.artwork?.length > 0) s.add(e.element_id); });
    return s;
  }, [sceneData]);

  // All placeable targets for the dropdown switcher in art placement mode
  const allPlacementTargets = useMemo(() => {
    const targets = [];
    // Pods
    Object.entries(podGroups).forEach(([podName, podGroup]) => {
      const hasArt = podGroup.walls.some(w => wallsWithArtwork.has(w.element_id));
      const artCount = podGroup.walls.reduce((n, w) => {
        const sd = (sceneData?.walls || []).find(sw => sw.element_id === w.element_id);
        return n + (sd?.artwork?.length || 0);
      }, 0);
      targets.push({ key: `pod:${podName}`, type: 'pod', label: podName, hasArt, artCount, podName, walls: podGroup.walls });
    });
    // Standalone walls
    (walls || []).forEach(wall => {
      try {
        const meta = wall.text_content ? JSON.parse(wall.text_content) : null;
        if (meta?.pod_group) return; // skip pod members
      } catch { /* ok */ }
      const hasArt = wallsWithArtwork.has(wall.element_id);
      const sd = (sceneData?.walls || []).find(sw => sw.element_id === wall.element_id);
      const artCount = sd?.artwork?.length || 0;
      targets.push({ key: `wall:${wall.element_id}`, type: 'wall', label: wall.source || 'Wall', hasArt, artCount, element: wall });
    });
    // Easels
    (easels || []).forEach(easel => {
      const hasArt = easelsWithArtwork.has(easel.element_id);
      const sd = (sceneData?.easels || []).find(se => se.element_id === easel.element_id);
      const artCount = sd?.artwork?.length || 0;
      targets.push({ key: `easel:${easel.element_id}`, type: 'easel', label: easel.source || 'Easel', hasArt, artCount, element: easel });
    });
    return targets;
  }, [podGroups, walls, easels, wallsWithArtwork, easelsWithArtwork, sceneData]);

  // Switch placement target from the dropdown
  const handleSwitchPlacementTarget = useCallback((key) => {
    const target = allPlacementTargets.find(t => t.key === key);
    if (!target) return;
    setSelectedArtworkId(null);
    if (target.type === 'pod') {
      setPlacementTarget({ type: 'pod', podName: target.podName, walls: target.walls });
      setSelectedPod(target.podName);
      setSelectedWall(null);
      setSelectedEasel(null);
    } else if (target.type === 'wall') {
      setPlacementTarget({ type: 'wall', element: target.element });
      setSelectedWall(target.element);
      setSelectedPod(null);
      setSelectedEasel(null);
    } else if (target.type === 'easel') {
      setPlacementTarget({ type: 'easel', element: target.element });
      setSelectedEasel(target.element);
      setSelectedWall(null);
      setSelectedPod(null);
    }
  }, [allPlacementTargets]);

  // All artworks that can be placed (unplaced inventory items) — respects active tab
  const unplacedArtworks = useMemo(() => {
    if (!inventoryData) return [];
    const placed = new Set();
    (sceneData?.walls || []).forEach(w => (w.artwork || []).forEach(a => placed.add(a.vc_id)));
    (sceneData?.easels || []).forEach(e => (e.artwork || []).forEach(a => placed.add(a.vc_id)));
    const source = inventoryTab === 'pending' ? (inventoryData.pending || []) : (inventoryData.accepted || []);
    return source.filter(a => !placed.has(a.vc_id));
  }, [inventoryData, sceneData, inventoryTab]);

  const filteredUnplaced = useMemo(() => {
    if (!inventorySearch.trim()) return unplacedArtworks;
    const q = inventorySearch.toLowerCase();
    return unplacedArtworks.filter(a =>
      (a.title || '').toLowerCase().includes(q) ||
      (a.artist_name || '').toLowerCase().includes(q)
    );
  }, [unplacedArtworks, inventorySearch]);

  // Get artworks currently on the placement target
  const targetArtworks = useMemo(() => {
    if (!placementTarget) return [];
    if (placementTarget.type === 'wall') {
      const wall = (sceneData?.walls || []).find(w => w.element_id === placementTarget.element.element_id);
      return wall?.artwork || [];
    }
    if (placementTarget.type === 'easel') {
      const easel = (sceneData?.easels || []).find(e => e.element_id === placementTarget.element.element_id);
      return easel?.artwork || [];
    }
    if (placementTarget.type === 'pod') {
      // Aggregate artwork from all pod walls
      const allArt = [];
      (placementTarget.walls || []).forEach(pw => {
        const wall = (sceneData?.walls || []).find(w => w.element_id === pw.element_id);
        if (wall?.artwork) allArt.push(...wall.artwork);
      });
      return allArt;
    }
    return [];
  }, [placementTarget, sceneData]);

  // Handle placing artwork on the wall surface panel
  const handleArtPlacementDrop = useCallback(async (e) => {
    e.preventDefault();
    if (!draggingArtworkItem || !placementTarget) return;

    const surfaceEl = e.currentTarget;
    const rect = surfaceEl.getBoundingClientRect();
    const wall = placementTarget.element;
    const wallWidth = parseFloat(wall.width || 120);
    const wallHeight = parseFloat(wall.height || 96);
    const displayWidth = surfaceEl.offsetWidth;
    const displayHeight = surfaceEl.offsetHeight;
    const scaleX = displayWidth / wallWidth;
    const scaleY = displayHeight / wallHeight;

    const x = (e.clientX - rect.left) / scaleX;
    const y = (e.clientY - rect.top) / scaleY;
    const artW = draggingArtworkItem.width || 24;
    const artH = draggingArtworkItem.height || 24;
    const posX = Math.max(0, Math.min(x - artW / 2, wallWidth - artW));
    const posY = Math.max(0, Math.min(y - artH / 2, wallHeight - artH));
    const finalPosX = Math.round(posX * 100) / 100;
    const finalPosY = Math.round(posY * 100) / 100;

    try {
      if (placementTarget.type === 'easel') {
        const isFromInventory = draggingArtworkItem.isFromInventory || !draggingArtworkItem.parent_element_id;
        if (isFromInventory) {
          await createAndPlaceArtwork(collectionId, draggingArtworkItem.vc_id, wall.element_id, 0, 0, draggingArtworkItem);
        } else {
          await updateArtworkPosition(draggingArtworkItem.element_id, wall.element_id, 0, 0);
        }
      } else {
        const isFromInventory = draggingArtworkItem.isFromInventory || !draggingArtworkItem.parent_element_id;
        if (isFromInventory) {
          await createAndPlaceArtwork(collectionId, draggingArtworkItem.vc_id, wall.element_id, finalPosX, finalPosY, draggingArtworkItem);
        } else {
          // Optimistic local update for repositioning
          setSceneData(prev => ({
            ...prev,
            walls: (prev.walls || []).map(w => w.element_id === wall.element_id ? {
              ...w, artwork: (w.artwork || []).map(a => a.element_id.toString() === draggingArtworkItem.element_id.toString()
                ? { ...a, wall_position_x: finalPosX, wall_position_y: finalPosY } : a)
            } : w)
          }));
          await updateArtworkPosition(draggingArtworkItem.element_id, wall.element_id, finalPosX, finalPosY);
        }
      }
      if (onRefreshData) await onRefreshData();
    } catch (err) {
      console.error('Error placing artwork:', err);
    }
    setDraggingArtworkItem(null);
    setIsDraggingArtwork(false);
  }, [draggingArtworkItem, placementTarget, collectionId, onRefreshData]);

  // Pod-specific drop: receives the specific wall within the pod
  const handlePodWallDrop = useCallback(async (e, wall) => {
    e.preventDefault();
    if (!draggingArtworkItem) return;

    const surfaceEl = e.currentTarget;
    const rect = surfaceEl.getBoundingClientRect();
    const wallWidth = parseFloat(wall.width || 96);
    const wallHeight = parseFloat(wall.height || 96);
    const scaleX = surfaceEl.offsetWidth / wallWidth;
    const scaleY = surfaceEl.offsetHeight / wallHeight;

    const x = (e.clientX - rect.left) / scaleX;
    const y = (e.clientY - rect.top) / scaleY;
    const artW = draggingArtworkItem.width || 24;
    const artH = draggingArtworkItem.height || 24;
    const posX = Math.round(Math.max(0, Math.min(x - artW / 2, wallWidth - artW)) * 100) / 100;
    const posY = Math.round(Math.max(0, Math.min(y - artH / 2, wallHeight - artH)) * 100) / 100;

    try {
      const isFromInventory = draggingArtworkItem.isFromInventory || !draggingArtworkItem.parent_element_id;
      if (isFromInventory) {
        await createAndPlaceArtwork(collectionId, draggingArtworkItem.vc_id, wall.element_id, posX, posY, draggingArtworkItem);
      } else {
        // Optimistic local update for repositioning
        setSceneData(prev => ({
          ...prev,
          walls: (prev.walls || []).map(w => w.element_id === wall.element_id ? {
            ...w, artwork: (w.artwork || []).map(a => a.element_id.toString() === draggingArtworkItem.element_id.toString()
              ? { ...a, wall_position_x: posX, wall_position_y: posY } : a)
          } : w)
        }));
        await updateArtworkPosition(draggingArtworkItem.element_id, wall.element_id, posX, posY);
      }
      if (onRefreshData) await onRefreshData();
    } catch (err) {
      console.error('Error placing artwork on pod wall:', err);
    }
    setDraggingArtworkItem(null);
    setIsDraggingArtwork(false);
  }, [draggingArtworkItem, collectionId, onRefreshData]);

  const handleRemoveArtworkFromTarget = useCallback(async (artworkId) => {
    if (onRemoveArtwork) {
      await onRemoveArtwork(artworkId);
      if (onRefreshData) await onRefreshData();
    }
  }, [onRemoveArtwork, onRefreshData]);

  // Nudge selected artwork with arrow keys (1" per press, 0.25" with shift)
  const handleNudgeArtwork = useCallback(async (artworkId, wallId, dx, dy) => {
    const wall = (sceneData?.walls || []).find(w => w.element_id === wallId);
    if (!wall?.artwork) return;
    const art = wall.artwork.find(a => a.element_id.toString() === artworkId.toString());
    if (!art) return;

    const curX = art.wall_position_x ?? 0;
    const curY = art.wall_position_y ?? 0;
    const newX = Math.round(Math.max(0, Math.min(curX + dx, (wall.width || 120) - (art.width || 24))) * 100) / 100;
    const newY = Math.round(Math.max(0, Math.min(curY + dy, (wall.height || 96) - (art.height || 24))) * 100) / 100;
    if (newX === curX && newY === curY) return;

    // Optimistic local update
    setSceneData(prev => ({
      ...prev,
      walls: (prev.walls || []).map(w => w.element_id === wallId ? {
        ...w, artwork: w.artwork.map(a => a.element_id.toString() === artworkId.toString()
          ? { ...a, wall_position_x: newX, wall_position_y: newY } : a)
      } : w)
    }));
    try {
      await updateArtworkPosition(artworkId, wallId, newX, newY);
    } catch (err) {
      console.error('Error nudging artwork:', err);
      if (onRefreshData) onRefreshData();
    }
  }, [sceneData, setSceneData, onRefreshData]);

  const handleSwapArtworkDimensions = useCallback(async (art) => {
    if (!art?.vc_id || !art.width || !art.height) return;
    try {
      await swapArtworkDimensions(art.vc_id, art.element_id, art.width, art.height);
      if (onRefreshData) await onRefreshData();
    } catch (error) {
      console.error('Error swapping artwork dimensions:', error);
      alert('Failed to swap dimensions. Please try again.');
    }
  }, [onRefreshData]);

  // Arrow key listener for selected artwork
  useEffect(() => {
    if (!selectedArtworkId || !placementTarget) return;
    const handleKey = (e) => {
      const amount = e.shiftKey ? 0.25 : 1;
      let dx = 0, dy = 0;
      switch (e.key) {
        case 'ArrowUp': dy = -amount; break;
        case 'ArrowDown': dy = amount; break;
        case 'ArrowLeft': dx = -amount; break;
        case 'ArrowRight': dx = amount; break;
        case 'Escape': setSelectedArtworkId(null); return;
        default: return;
      }
      e.preventDefault();
      // Find which wall the artwork is on
      let wallId = null;
      if (placementTarget.type === 'wall') {
        wallId = placementTarget.element.element_id;
      } else if (placementTarget.type === 'pod') {
        for (const pw of (placementTarget.walls || [])) {
          const wall = (sceneData?.walls || []).find(w => w.element_id === pw.element_id);
          if (wall?.artwork?.some(a => a.element_id.toString() === selectedArtworkId.toString())) {
            wallId = wall.element_id;
            break;
          }
        }
      }
      if (wallId) handleNudgeArtwork(selectedArtworkId, wallId, dx, dy);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [selectedArtworkId, placementTarget, sceneData, handleNudgeArtwork]);

  // ── Artist Search handlers ──
  const handleArtistSearch = async (e) => {
    e.preventDefault();
    if (!artistSearchQuery.trim()) return;
    try {
      setIsSearchingArtist(true);
      setSearchStatusMsg(null);
      const results = await searchArtistArtwork(artistSearchQuery.trim());
      setArtistSearchResults(results);
      if (!results.artist) {
        setSearchStatusMsg(`No artist found for "${artistSearchQuery}"`);
      } else if (results.artworks.length === 0) {
        setSearchStatusMsg(`${results.artist.handle || results.artist.name} has no public artworks for sale`);
      }
    } catch (err) {
      console.error('Error searching artist:', err);
      setSearchStatusMsg('Search failed — check console');
    } finally {
      setIsSearchingArtist(false);
    }
  };

  const handleAddArtworkToCollection = async (artwork) => {
    if (!collectionId) return;
    try {
      setAddingArtworkId(artwork.vc_id);
      const result = await addArtworkToCollection(collectionId, artwork);
      if (result.existing) {
        setSearchStatusMsg('Artwork already in collection');
      } else if (result.success) {
        const newElement = { ...result.element, title: artwork.title, price: artwork.price, pendingApproval: result.pendingApproval };
        setSceneData(prev => ({ ...prev, unplaced: [...(prev.unplaced || []), newElement] }));
        setSearchStatusMsg(`Added "${artwork.title}"${result.pendingApproval ? ' (pending approval)' : ''}`);
        if (onRefreshData) onRefreshData();
      }
    } catch (err) {
      console.error('Error adding artwork:', err);
      setSearchStatusMsg('Failed to add artwork');
    } finally {
      setAddingArtworkId(null);
    }
  };

  const isArtworkInCollection = useCallback((vcId) => {
    const inWalls = (sceneData?.walls || []).some(wall => wall.artwork?.some(art => art.vc_id === vcId));
    const inEasels = (sceneData?.easels || []).some(easel => easel.artwork?.some(art => art.vc_id === vcId));
    const inUnplaced = sceneData?.unplaced?.some(art => art.vc_id === vcId);
    const inInvAccepted = (inventoryData?.accepted || []).some(a => a.vc_id === vcId);
    const inInvPending = (inventoryData?.pending || []).some(a => a.vc_id === vcId);
    return inWalls || inEasels || inUnplaced || inInvAccepted || inInvPending;
  }, [sceneData, inventoryData]);

  const clearArtistSearch = () => {
    setArtistSearchQuery('');
    setArtistSearchResults(null);
    setArtworkFilterQuery('');
    setSearchStatusMsg(null);
  };

  const enterPlacementMode = useCallback(() => {
    setPlacementMode(true);
    if (selectedPod && podGroups[selectedPod]) {
      setPlacementTarget({ type: 'pod', podName: selectedPod, walls: podGroups[selectedPod].walls });
    } else if (selectedWall) {
      setPlacementTarget({ type: 'wall', element: selectedWall });
    } else if (selectedEasel) {
      setPlacementTarget({ type: 'easel', element: selectedEasel });
    } else {
      setPlacementTarget(null);
    }
    // Clear all canvas selections so floating D-pad overlays don't linger behind the placement UI
    setSelectedWall(null);
    setSelectedEasel(null);
    setSelectedStructure(null);
    setSelectedPod(null);
    setMultiSelected([]);
  }, [selectedWall, selectedEasel, selectedPod, podGroups]);

  const exitPlacementMode = useCallback(() => {
    setPlacementMode(false);
    setPlacementTarget(null);
    setDraggingArtworkItem(null);
    setIsDraggingArtwork(false);
    setInventorySearch('');
  }, []);

  // Keep placement target in sync with sceneData (only re-sync when sceneData refreshes)
  useEffect(() => {
    if (!placementMode) return;
    setPlacementTarget(prev => {
      if (!prev) return prev;
      if (prev.type === 'wall') {
        const fresh = (sceneData?.walls || []).find(w => w.element_id === prev.element.element_id);
        if (fresh && fresh !== prev.element) {
          return { ...prev, element: fresh };
        }
      } else if (prev.type === 'easel') {
        const fresh = (sceneData?.easels || []).find(e => e.element_id === prev.element.element_id);
        if (fresh && fresh !== prev.element) {
          return { ...prev, element: fresh };
        }
      } else if (prev.type === 'pod') {
        const freshWalls = (prev.walls || []).map(pw =>
          (sceneData?.walls || []).find(w => w.element_id === pw.element_id) || pw
        );
        const changed = freshWalls.some((fw, i) => fw !== prev.walls[i]);
        if (changed) return { ...prev, walls: freshWalls };
      }
      return prev;
    });
  }, [sceneData, placementMode]);

  const getImgUrl = useCallback((artwork, useCase = 'thumbnail') => {
    const src = artwork?.source || artwork?.image_url || '';
    if (getOptimizedImageUrl) return getOptimizedImageUrl(src, useCase);
    return src;
  }, [getOptimizedImageUrl]);

  // Close layout search dropdown on click outside
  useEffect(() => {
    if (!layoutSearchOpen) return;
    const handleClickOutside = (e) => {
      if (layoutSearchRef.current && !layoutSearchRef.current.contains(e.target)) {
        setLayoutSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [layoutSearchOpen]);

  // Handle selecting a search result to navigate to its location
  const handleLayoutSearchSelect = useCallback((item) => {
    setLayoutSearchQuery('');
    setLayoutSearchOpen(false);
    let highlightKey = null;
    if (item.locationType === 'pod' && item.podName) {
      setSelectedPod(item.podName);
      setSelectedWall(null);
      setSelectedEasel(null);
      setSelectedStructure(null);
      highlightKey = { type: 'pod', id: item.podName };
    } else if (item.locationType === 'wall' && item.wallObj) {
      setSelectedWall(item.wallObj);
      setSelectedPod(null);
      setSelectedEasel(null);
      setSelectedStructure(null);
      highlightKey = { type: 'wall', id: String(item.wallObj.element_id) };
    } else if (item.locationType === 'easel' && item.easelObj) {
      setSelectedEasel(item.easelObj);
      setSelectedWall(null);
      setSelectedPod(null);
      setSelectedStructure(null);
      highlightKey = { type: 'easel', id: String(item.easelObj.element_id) };
    }
    setMultiSelected([]);
    if (highlightKey) {
      setSearchHighlight(highlightKey);
      setTimeout(() => setSearchHighlight(null), 2000);
    }
  }, []);

  // Generic floating D-pad overlay helper used for selected pods/walls/easels/structures.
  // Caller provides the screen-space bbox (in canvas px), label, and move/rotate/deselect callbacks.
  const renderFloatingDPad = ({ keyId, bbox, label, onMove, onRotate, onDeselect }) => {
    const OVERLAY_W = 132;
    const OVERLAY_H_EST = 168;
    const GAP = 12;
    const wantTopAbove = bbox.topLeftY - GAP - OVERLAY_H_EST;
    const flipBelow = wantTopAbove < 0;
    const anchorLeft = bbox.topLeftX + (bbox.widthPx / 2) - (OVERLAY_W / 2);
    const anchorTop = flipBelow ? bbox.topLeftY + bbox.heightPx + GAP : wantTopAbove;
    const overlayLeft = anchorLeft + floatingOverlayOffset.x;
    const overlayTop = anchorTop + floatingOverlayOffset.y;

    // Press-and-hold: tap = single step, hold (>280ms) = repeat ×10 step every 180ms
    const makeHoldHandlers = (onTap, onHold) => {
      let holdTimer = null;
      let repeatTimer = null;
      let didHold = false;
      const cleanup = () => {
        if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
        if (repeatTimer) { clearInterval(repeatTimer); repeatTimer = null; }
        window.removeEventListener('mouseup', up);
        window.removeEventListener('mouseleave', up);
      };
      const up = () => {
        if (!didHold) onTap();
        cleanup();
      };
      return {
        onMouseDown: (e) => {
          e.preventDefault();
          e.stopPropagation();
          didHold = false;
          holdTimer = setTimeout(() => {
            didHold = true;
            onHold();
            repeatTimer = setInterval(onHold, 180);
          }, 280);
          window.addEventListener('mouseup', up);
          window.addEventListener('mouseleave', up);
        }
      };
    };

    const dirBtn = (glyph, dx, dz, title) => {
      const handlers = makeHoldHandlers(
        () => onMove(dx, dz),
        () => onMove(dx * 10, dz * 10)
      );
      return (
        <button
          {...handlers}
          title={title}
          style={{
            width: '36px', height: '36px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '6px', border: '1px solid #0ea5e9',
            background: '#fff', cursor: 'pointer',
            fontSize: '20px', lineHeight: 1, fontWeight: 700, color: '#0ea5e9',
            userSelect: 'none'
          }}
        >{glyph}</button>
      );
    };

    const startHeaderDrag = (e) => {
      e.preventDefault();
      e.stopPropagation();
      floatingOverlayDragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        baseX: floatingOverlayOffset.x,
        baseY: floatingOverlayOffset.y
      };
      const onMoveDrag = (mv) => {
        const ref = floatingOverlayDragRef.current;
        if (!ref) return;
        setFloatingOverlayOffset({
          x: ref.baseX + (mv.clientX - ref.startX),
          y: ref.baseY + (mv.clientY - ref.startY)
        });
      };
      const onUp = () => {
        floatingOverlayDragRef.current = null;
        window.removeEventListener('mousemove', onMoveDrag);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMoveDrag);
      window.addEventListener('mouseup', onUp);
    };

    return (
      <div
        key={keyId}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          left: `${overlayLeft}px`,
          top: `${overlayTop}px`,
          width: `${OVERLAY_W}px`,
          background: 'rgba(255, 255, 255, 0.97)',
          border: '1px solid #0ea5e9',
          borderRadius: '8px',
          boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
          padding: '6px',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}
      >
        <div
          onMouseDown={startHeaderDrag}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px', cursor: 'grab', padding: '0 2px' }}
        >
          <span style={{ fontSize: '11px', fontWeight: 700, color: '#0ea5e9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={`${label} — drag to move panel`}>
            ⋮⋮ {label}
          </span>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onDeselect}
            title="Deselect"
            style={{ padding: '1px 6px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#f9fafb', cursor: 'pointer', fontSize: '11px', lineHeight: 1, fontWeight: 700, color: '#6b7280' }}
          >✕</button>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 36px)',
          gridTemplateRows: 'repeat(3, 36px)',
          gap: '4px',
          justifyContent: 'center'
        }}>
          <div />
          {dirBtn('↑', 0, -1, 'Up — tap −1", hold −10"')}
          <div />
          {dirBtn('←', -1, 0, 'Left — tap −1", hold −10"')}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => onRotate(-15)}
            title="Rotate −15° (counter-clockwise)"
            style={{
              width: '36px', height: '36px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '6px', border: '1px solid #0ea5e9',
              background: '#fff', cursor: 'pointer',
              fontSize: '20px', lineHeight: 1, fontWeight: 700, color: '#0ea5e9',
              userSelect: 'none'
            }}
          >⟲</button>
          {dirBtn('→', 1, 0, 'Right — tap +1", hold +10"')}
          <div />
          {dirBtn('↓', 0, 1, 'Down — tap +1", hold +10"')}
          <div />
        </div>
      </div>
    );
  };


  return (
    <div className="top-down-planner-overlay">
      <div className="top-down-planner-modal">
        <div className="top-down-planner-header">
          <div className="header-left">
            {!placementMode ? (
              <div className="layout-search-container" ref={layoutSearchRef}>
                <div className="layout-search-input-wrap">
                  <Search size={16} className="layout-search-icon" />
                  <input
                    type="text"
                    className="layout-search-input"
                    placeholder="Search placed artwork by title or artist…"
                    value={layoutSearchQuery}
                    onChange={(e) => {
                      setLayoutSearchQuery(e.target.value);
                      setLayoutSearchOpen(true);
                    }}
                    onFocus={() => { if (layoutSearchQuery.trim()) setLayoutSearchOpen(true); }}
                  />
                  {layoutSearchQuery && (
                    <button className="layout-search-clear" onClick={() => { setLayoutSearchQuery(''); setLayoutSearchOpen(false); }}>
                      <X size={14} />
                    </button>
                  )}
                </div>
                {layoutSearchOpen && layoutSearchQuery.trim() && (
                  <div className="layout-search-dropdown">
                    {layoutSearchResults.length === 0 ? (
                      <div className="layout-search-empty">No placed artwork found</div>
                    ) : (
                      layoutSearchResults.map((item, idx) => (
                        <button
                          key={`${item.artwork.element_id}-${idx}`}
                          className="layout-search-result"
                          onClick={() => handleLayoutSearchSelect(item)}
                        >
                          {item.artwork.source && (
                            <img
                              src={getImgUrl(item.artwork, 'thumbnail')}
                              alt=""
                              className="layout-search-thumb"
                            />
                          )}
                          <div className="layout-search-result-info">
                            <span className="layout-search-result-title">{item.artwork.title || 'Untitled'}</span>
                            <span className="layout-search-result-artist">{item.artwork.artist_name || 'Unknown artist'}</span>
                          </div>
                          <span className={`layout-search-result-location ${item.locationType}`}>
                            {item.locationType === 'easel' ? '🖼 ' : item.locationType === 'pod' ? '⬡ ' : '🧱 '}
                            {item.locationLabel}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              <h2>Top-Down Wall Planner</h2>
            )}
            <p>{placementMode ? 'Click a wall/easel on floor plan to select it, then drag artwork from inventory' : 'Drag to reposition | Click to select | Shift-click to multi-select | Scroll to zoom'}</p>
          </div>
          <div className="header-right">
            {/* Tools Dropdown */}
            {toolsActions && (
              <ToolsDropdown toolsActions={toolsActions} onClose={onClose} onDone={handleDone} />
            )}
            {/* Mode tabs — always show both when placement is available */}
            {placementAvailable && (
              <div className="mode-tabs">
                <button
                  className={`mode-tab ${!placementMode ? 'active' : ''}`}
                  onClick={placementMode ? exitPlacementMode : undefined}
                >
                  <Layout size={15} /> Layout
                </button>
                {!placementMode && (
                  <button
                    className={`mode-tab inventory-toggle ${showLayoutInventory ? 'active' : ''}`}
                    onClick={() => setShowLayoutInventory(v => !v)}
                    title="Toggle artwork inventory preview"
                  >
                    <List size={15} /> Inventory
                  </button>
                )}
                {!placementMode && (easels || []).length > 0 && (
                  <button
                    className={`mode-tab inventory-toggle ${showEaselGallery ? 'active' : ''}`}
                    onClick={() => setShowEaselGallery(v => !v)}
                    title="Toggle easel gallery strip"
                    style={showEaselGallery ? { borderColor: '#d97706', color: '#92400e' } : {}}
                  >
                    <Eye size={15} /> Easels
                  </button>
                )}
                <button
                  className={`mode-tab ${placementMode ? 'active' : ''}`}
                  onClick={!placementMode ? enterPlacementMode : undefined}
                >
                  <Image size={15} /> Art Placement
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className={`top-down-planner-content ${placementMode ? 'placement-mode-active' : ''}`}>
          <div className="planner-main-row">
          {/* Gallery Canvas */}
          <div 
            ref={canvasRef}
            className="gallery-canvas"
            style={{
              cursor: isPanning ? 'grabbing' : 'grab'
            }}
            onMouseDown={handlePanStart}
            onMouseMove={handlePanMove}
            onMouseUp={handlePanEnd}
            onMouseLeave={handlePanEnd}

            onClick={handleCanvasClick}
          >
            {/* Gallery boundaries */}
            <div className="gallery-boundary">
              <div className="boundary-label">Gallery Space</div>
              <div className="scale-info">
                Scale: 1 inch = {finalScale.toFixed(2)} pixel{finalScale !== 1 ? 's' : ''}
              </div>
            </div>
            

            
            {/* Navigation Controls */}
            <div className="navigation-controls">
              <button
                className="nav-btn fit-btn"
                onClick={fitToScreen}
                title="Fit to Screen"
              >
                <span className="icon-text">⛶</span>
                <span className="button-text">Fit to Screen</span>
              </button>
            </div>

            {/* Deselect Button - Only show when a wall, easel, structure, or multi-selection is active */}
            {(selectedWall || selectedEasel || selectedStructure || multiSelected.length > 0) && (
              <button
                className="deselect-btn"
                onClick={() => {
                  console.log('🔄 Deselecting via button');
                  setSelectedWall(null);
                  setSelectedEasel(null);
                  setSelectedStructure(null);
                  clearMultiSelect();
                }}
                title="Deselect"
                style={{
                  position: 'absolute',
                  top: '120px',
                  left: '10px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  zIndex: 100
                }}
              >
                ✕ Deselect{multiSelected.length > 1 ? ` (${multiSelected.length})` : ''}
              </button>
            )}

            {/* Duplicate Button — show when any item or multi-selection is active */}
            {(selectedWall || selectedPod || selectedEasel || selectedStructure || multiSelected.length > 0) && (
              <button
                onClick={(e) => { e.stopPropagation(); handleDuplicate(); }}
                title="Duplicate selected item(s) — offset to the right"
                style={{
                  position: 'absolute',
                  top: '160px',
                  left: '10px',
                  background: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  zIndex: 100
                }}
              >
                ⧉ Duplicate{multiSelected.length > 1 ? ` (${multiSelected.length})` : ''}
              </button>
            )}
            
            {/* Add Wall Button */}
            <button
              className="add-wall-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowAddWallModal(true);
              }}
              title="Add New Wall"
            >
              <Plus size={20} />
              Add Wall
            </button>
            
            {/* Add Pod Button */}
            {onAddPod && (
              <button
                className="add-wall-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddPodModal(true);
                }}
                title="Add New Pod"
                style={{ right: '140px' }}
              >
                <Plus size={20} />
                Add Pod
              </button>
            )}
            
            {/* Add Easel Button */}
            {onAddEasel && (
              <button
                className="add-wall-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddEaselModal(true);
                }}
                title="Add New Easel"
                style={{ right: '260px' }}
              >
                <Plus size={20} />
                Add Easel
              </button>
            )}
            
            {/* Add Structure Button */}
            {onAddStructure && (
              <button
                className="add-wall-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddStructureModal(true);
                }}
                title="Add Column / Solid Wall"
                style={{ right: '380px' }}
              >
                <Plus size={20} />
                Add Structure
              </button>
            )}
            
            {/* Render walls (skip pod member walls — they're rendered as pod outlines) */}
            {walls.filter(wall => {
              try {
                const meta = wall.text_content ? JSON.parse(wall.text_content) : null;
                return !meta?.pod_group;
              } catch { return true; }
            }).map((wall) => {
              const wallInfo = getWallDisplayInfo(wall);
              const isSelected = selectedWall?.element_id === wall.element_id;
              const isMultiSel = isMultiSelectedItem('wall', wall.element_id);
              // Apply multi-drag delta offset
              const mdx = (isMultiDragging && isMultiSel) ? multiDragDelta.x : 0;
              const mdz = (isMultiDragging && isMultiSel) ? multiDragDelta.z : 0;
              const wallCanvasX = wallInfo.canvasPos.x + mdx * finalScale;
              const wallCanvasY = wallInfo.canvasPos.y + mdz * finalScale;
              
              return (
                <div
                  key={wall.element_id}
                  className={`wall-top-down ${isSelected ? 'selected' : ''} ${searchHighlight?.type === 'wall' && searchHighlight.id === String(wall.element_id) ? 'search-highlight' : ''}`}
                  style={{
                    position: 'absolute',
                    left: `${wallCanvasX}px`,
                    top: `${wallCanvasY}px`,
                    transform: `translate(-50%, -50%) rotate(${wallInfo.rotationY}deg)`,
                    cursor: isDragging ? 'grabbing' : 'grab',
                    zIndex: isDragging ? 1000 : (isSelected ? 100 : 10)
                  }}
                  onClick={() => handleWallClick(wall)}
                  onMouseDown={(e) => {
                    if (e.shiftKey) {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleMultiSelect('wall', wall.element_id);
                      return;
                    }
                    if (multiSelected.length > 0 && isMultiSelectedItem('wall', wall.element_id)) {
                      handleMultiDragStart(e);
                      return;
                    }
                    handleWallMouseDown(e, wall);
                  }}
                  title={`${wall.source} | X: ${wallInfo.centerX.toFixed(1)}" Y: ${(parseFloat(wall.wall_center_y) || 72).toFixed(1)}" Z: ${wallInfo.centerZ.toFixed(1)}" | Rot: ${wallInfo.rotationY.toFixed(1)}°`}
                >
                  {/* Wall rectangle */}
                  <div 
                    className="wall-rectangle"
                    style={{
                      width: `${wallInfo.width * finalScale}px`,
                      height: `${wallInfo.depth * finalScale}px`,
                      backgroundColor: isMultiSel ? '#0e7490'
                        : isSelected ? '#4f46e5'
                        : wallsWithArtwork.has(wall.element_id) ? '#059669'
                        : (wall.background_color || '#6b7280'),
                      border: isMultiSel ? '2px solid #06b6d4'
                        : isSelected ? '2px solid #1e40af'
                        : wallsWithArtwork.has(wall.element_id) ? '2px solid #10b981'
                        : '1px solid #374151',
                      borderRadius: '2px'
                    }}
                  />
                  

                </div>
              );
            })}
            
            {/* Render pod outlines with wall segments (draggable as group) */}
            {Object.entries(podGroups).map(([podName, podGroup]) => {
              // Apply drag delta if this pod is being dragged
              const draggedWalls = (isDraggingPod && selectedPod === podName)
                ? podGroup.walls.map(w => ({
                    ...w,
                    wall_center_x: (parseFloat(w.wall_center_x) || 0) + podDragDelta.x,
                    wall_center_z: (parseFloat(w.wall_center_z) || 0) + podDragDelta.z
                  }))
                : podGroup.walls;
              const bbox = getPodBoundingBox(draggedWalls);
              const topLeft = galleryToCanvas(bbox.minX, bbox.minZ);
              const bboxWidthPx = bbox.width * finalScale;
              const bboxDepthPx = bbox.depth * finalScale;
              const isSelected = selectedPod === podName;
              const isPodMultiSel = isMultiSelectedItem('pod', podName);
              // Apply multi-drag delta for pods
              const podMdx = (isMultiDragging && isPodMultiSel) ? multiDragDelta.x * finalScale : 0;
              const podMdz = (isMultiDragging && isPodMultiSel) ? multiDragDelta.z * finalScale : 0;
              return (
                <div
                  key={`pod-bbox-${podName}`}
                  className={searchHighlight?.type === 'pod' && searchHighlight.id === podName ? 'search-highlight' : ''}
                  style={{
                    position: 'absolute',
                    left: `${topLeft.x + podMdx}px`,
                    top: `${topLeft.y + podMdz}px`,
                    width: `${bboxWidthPx}px`,
                    height: `${bboxDepthPx}px`,
                    border: 'none',
                    outline: isPodMultiSel ? '2px solid rgba(6, 182, 212, 0.5)' : 'none',
                    outlineOffset: isPodMultiSel ? '0px' : undefined,
                    borderRadius: '4px',
                    cursor: isDraggingPod ? 'grabbing' : 'grab',
                    zIndex: isDraggingPod && isSelected ? 1000 : 15,
                    backgroundColor: isPodMultiSel ? 'rgba(6, 182, 212, 0.06)' : 'transparent'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPod(podName);
                    setPodRenameValue(null);
                    setSelectedWall(null);
                    setSelectedEasel(null);
                    setSelectedStructure(null);
                    if (placementMode && podGroup.walls.length > 0) {
                      setPlacementTarget({ type: 'pod', podName, walls: podGroup.walls });
                    }
                  }}
                  onMouseDown={(e) => {
                    if (e.shiftKey) {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleMultiSelect('pod', podName);
                      return;
                    }
                    if (multiSelected.length > 0 && isMultiSelectedItem('pod', podName)) {
                      handleMultiDragStart(e);
                      return;
                    }
                    handlePodMouseDown(e, podName);
                  }}
                  title={`${podName} | Tent Panel | Center: (${bbox.centerX.toFixed(0)}", ${bbox.centerZ.toFixed(0)}")`}
                >
                  {/* Render tent-panel footprint for EACH wall in the pod */}
                  {draggedWalls.map((podWall) => {
                    const wcx = parseFloat(podWall.wall_center_x) || 0;
                    const wcz = parseFloat(podWall.wall_center_z) || 0;
                    const wWidth = parseFloat(podWall.width) || 48;
                    const wRot = parseFloat(podWall.wall_rotation_y) || 0;
                    let tiltDeg = 70;
                    try {
                      const meta = podWall.text_content ? JSON.parse(podWall.text_content) : null;
                      tiltDeg = meta?.tilt_angle || 70;
                    } catch { /* ignore */ }
                    const tiltRad = (tiltDeg * Math.PI) / 180;
                    const wDepth = (parseFloat(podWall.height) || 96) * Math.cos(tiltRad);
                    const relX = (wcx - bbox.minX) * finalScale;
                    const relZ = (wcz - bbox.minZ) * finalScale;
                    const segWidthPx = wWidth * finalScale;
                    const segDepthPx = Math.max(wDepth * finalScale, 3);
                    const hasArt = wallsWithArtwork.has(podWall.element_id);
                    const baseColor = isSelected
                      ? '#0ea5e9'
                      : hasArt ? '#10b981' : '#8b5cf6';
                    return (
                      <div
                        key={`pod-panel-${podWall.element_id}`}
                        style={{
                          position: 'absolute',
                          left: `${relX}px`,
                          top: `${relZ}px`,
                          width: `${segWidthPx}px`,
                          height: `${segDepthPx}px`,
                          transform: `translate(-50%, -50%) rotate(${wRot}deg)`,
                          borderRadius: '2px',
                          pointerEvents: 'none',
                          opacity: isSelected ? 1 : 0.6,
                          background: isSelected
                            ? baseColor
                            : `linear-gradient(to bottom, ${baseColor} 0%, transparent 100%)`
                        }}
                      >
                        {/* Ridge line at the top (peak of tent) */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '2px',
                          backgroundColor: baseColor,
                          opacity: 1
                        }} />
                      </div>
                    );
                  })}

                </div>
              );
            })}
            
            {/* Render easels */}
            {(easels || []).map((easel) => {
              const cx = parseFloat(isDraggingEasel && selectedEasel?.element_id === easel.element_id ? selectedEasel.position_x : easel.position_x) || 0;
              const cz = parseFloat(isDraggingEasel && selectedEasel?.element_id === easel.element_id ? selectedEasel.position_z : easel.position_z) || 0;
              const rotY = parseFloat(easel.rotation_y) || 0;
              const canvasPos = galleryToCanvas(cx, cz);
              const isSelected = selectedEasel?.element_id === easel.element_id;
              const isEaselMultiSel = isMultiSelectedItem('easel', easel.element_id);
              const easelSize = 12 * finalScale; // 12" footprint
              const eMdx = (isMultiDragging && isEaselMultiSel) ? multiDragDelta.x * finalScale : 0;
              const eMdz = (isMultiDragging && isEaselMultiSel) ? multiDragDelta.z * finalScale : 0;
              
              return (
                <div
                  key={`easel-${easel.element_id}`}
                  className={searchHighlight?.type === 'easel' && searchHighlight.id === String(easel.element_id) ? 'search-highlight' : ''}
                  style={{
                    position: 'absolute',
                    left: `${canvasPos.x + eMdx}px`,
                    top: `${canvasPos.y + eMdz}px`,
                    transform: `translate(-50%, -50%) rotate(${-rotY}deg)`,
                    cursor: isDraggingEasel ? 'grabbing' : 'grab',
                    zIndex: isSelected ? 100 : 15
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (e.shiftKey) {
                      toggleMultiSelect('easel', easel.element_id);
                      return;
                    }
                    setSelectedEasel(easel); setSelectedWall(null); setSelectedPod(null); setSelectedStructure(null);
                    if (placementMode) setPlacementTarget({ type: 'easel', element: easel });
                  }}
                  onMouseDown={(e) => {
                    if (e.shiftKey) return;
                    if (multiSelected.length > 0 && isMultiSelectedItem('easel', easel.element_id)) {
                      handleMultiDragStart(e);
                      return;
                    }
                    handleEaselMouseDown(e, easel);
                  }}
                  title={`${easel.source || 'Easel'} | X: ${cx.toFixed(1)}" Z: ${cz.toFixed(1)}" | Rot: ${rotY}°`}
                >
                  {/* Easel — solid square, green if has artwork */}
                  <div style={{
                    width: `${easelSize}px`,
                    height: `${easelSize}px`,
                    backgroundColor: isEaselMultiSel ? '#0891b2'
                      : isSelected ? '#3b82f6'
                      : easelsWithArtwork.has(easel.element_id) ? '#059669'
                      : '#2563eb',
                    border: isEaselMultiSel ? '2px solid #06b6d4'
                      : isSelected ? '2px solid #93c5fd'
                      : easelsWithArtwork.has(easel.element_id) ? '1px solid #047857'
                      : '1px solid #1d4ed8',
                    borderRadius: '3px',
                    opacity: 0.85
                  }} />

                </div>
              );
            })}
            
            {/* Render structures (columns, solid walls) */}
            {(structures || []).map((structure) => {
              const s = selectedStructure?.element_id === structure.element_id ? selectedStructure : structure;
              const cx = parseFloat(s.position_x) || 0;
              const cz = parseFloat(s.position_z) || 0;
              const rotY = parseFloat(s.rotation_y) || 0;
              const canvasPos = galleryToCanvas(cx, cz);
              const isSelected = selectedStructure?.element_id === structure.element_id;
              const isStructMultiSel = isMultiSelectedItem('structure', structure.element_id);
              const sMdx = (isMultiDragging && isStructMultiSel) ? multiDragDelta.x * finalScale : 0;
              const sMdz = (isMultiDragging && isStructMultiSel) ? multiDragDelta.z * finalScale : 0;
              const sWidth = (parseFloat(s.width) || 12) * finalScale;
              const sDepth = (parseFloat(s.depth) || 12) * finalScale;
              let meta;
              try { meta = s.text_content ? JSON.parse(s.text_content) : {}; } catch { meta = {}; }
              const shape = meta.shape || 'column-square';
              const isRound = shape === 'column-round';
              
              return (
                <div
                  key={`structure-${structure.element_id}`}
                  style={{
                    position: 'absolute',
                    left: `${canvasPos.x + sMdx}px`,
                    top: `${canvasPos.y + sMdz}px`,
                    transform: `translate(-50%, -50%) rotate(${rotY}deg)`,
                    cursor: isDraggingStructure ? 'grabbing' : 'grab',
                    zIndex: isSelected ? 100 : 12
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (e.shiftKey) {
                      toggleMultiSelect('structure', structure.element_id);
                      return;
                    }
                    setSelectedStructure(structure); setSelectedWall(null); setSelectedEasel(null); setSelectedPod(null);
                  }}
                  onMouseDown={(e) => {
                    if (e.shiftKey) return;
                    if (multiSelected.length > 0 && isMultiSelectedItem('structure', structure.element_id)) {
                      handleMultiDragStart(e);
                      return;
                    }
                    e.stopPropagation();
                    e.preventDefault();
                    hasDraggedRef.current = false;
                    setIsDraggingStructure(true);
                    setStructureDragStart({ x: e.clientX, y: e.clientY });
                    setSelectedStructure(structure);
                    setSelectedWall(null);
                    setSelectedEasel(null);
                    setSelectedPod(null);
                  }}
                  title={`${s.source || 'Structure'} (${shape}) | ${s.width}"×${s.depth}" | X: ${cx.toFixed(0)}" Z: ${cz.toFixed(0)}"`}
                >
                  <div style={{
                    width: `${Math.max(sWidth, 4)}px`,
                    height: `${Math.max(sDepth, 4)}px`,
                    backgroundColor: isStructMultiSel ? '#0e7490' : isSelected ? '#f59e0b' : (s.color || '#9ca3af'),
                    border: isStructMultiSel ? '2px solid #06b6d4' : isSelected ? '2px solid #d97706' : '1px solid #6b7280',
                    borderRadius: isRound ? '50%' : '2px',
                    opacity: 0.85
                  }} />
                </div>
              );
            })}

            {/* Floating D-pad overlay for the currently selected pod (single-select only) */}
            {!placementMode && selectedPod && podGroups[selectedPod] && multiSelected.length === 0 && (() => {
              const pg = podGroups[selectedPod];
              if (!pg.walls.length) return null;
              const liveWalls = isDraggingPod
                ? pg.walls.map(w => ({
                    ...w,
                    wall_center_x: (parseFloat(w.wall_center_x) || 0) + podDragDelta.x,
                    wall_center_z: (parseFloat(w.wall_center_z) || 0) + podDragDelta.z
                  }))
                : pg.walls;
              const bbox = getPodBoundingBox(liveWalls);
              const topLeft = galleryToCanvas(bbox.minX, bbox.minZ);
              return renderFloatingDPad({
                keyId: `pod-${selectedPod}`,
                bbox: { topLeftX: topLeft.x, topLeftY: topLeft.y, widthPx: bbox.width * finalScale, heightPx: bbox.depth * finalScale },
                label: selectedPod,
                onMove: (dx, dz) => handleMovePod(selectedPod, { dx, dz }),
                onRotate: (deg) => handleRotatePod(selectedPod, deg),
                onDeselect: () => { setSelectedPod(null); setPodRenameValue(null); }
              });
            })()}

            {/* Floating D-pad overlay for the currently selected easel */}
            {!placementMode && selectedEasel && multiSelected.length === 0 && (() => {
              const cx = parseFloat(selectedEasel.position_x) || 0;
              const cz = parseFloat(selectedEasel.position_z) || 0;
              const sizePx = 12 * finalScale;
              const center = galleryToCanvas(cx, cz);
              return renderFloatingDPad({
                keyId: `easel-${selectedEasel.element_id}`,
                bbox: { topLeftX: center.x - sizePx / 2, topLeftY: center.y - sizePx / 2, widthPx: sizePx, heightPx: sizePx },
                label: selectedEasel.source || 'Easel',
                onMove: (dx, dz) => {
                  const updated = {
                    ...selectedEasel,
                    position_x: (parseFloat(selectedEasel.position_x) || 0) + dx,
                    position_z: (parseFloat(selectedEasel.position_z) || 0) + dz
                  };
                  setSelectedEasel(updated);
                  if (onUpdateEasel) onUpdateEasel(updated);
                },
                onRotate: (deg) => {
                  const next = ((parseFloat(selectedEasel.rotation_y) || 0) + deg + 360) % 360;
                  const updated = { ...selectedEasel, rotation_y: next };
                  setSelectedEasel(updated);
                  if (onUpdateEasel) onUpdateEasel(updated);
                },
                onDeselect: () => setSelectedEasel(null)
              });
            })()}

            {/* Floating D-pad overlay for the currently selected wall */}
            {!placementMode && selectedWall && multiSelected.length === 0 && (() => {
              const wallInfo = getWallDisplayInfo(selectedWall);
              const rotY = wallInfo.rotationY || 0;
              const rad = (rotY * Math.PI) / 180;
              const c = Math.abs(Math.cos(rad));
              const s = Math.abs(Math.sin(rad));
              const aabbW = (wallInfo.width * c + wallInfo.depth * s) * finalScale;
              const aabbH = (wallInfo.width * s + wallInfo.depth * c) * finalScale;
              return renderFloatingDPad({
                keyId: `wall-${selectedWall.element_id}`,
                bbox: { topLeftX: wallInfo.canvasPos.x - aabbW / 2, topLeftY: wallInfo.canvasPos.y - aabbH / 2, widthPx: aabbW, heightPx: aabbH },
                label: selectedWall.source || 'Wall',
                onMove: (dx, dz) => {
                  const updated = {
                    ...selectedWall,
                    wall_center_x: (parseFloat(selectedWall.wall_center_x) || 0) + dx,
                    wall_center_z: (parseFloat(selectedWall.wall_center_z) || 0) + dz
                  };
                  setSelectedWall(updated);
                  if (onUpdateWall) onUpdateWall(updated);
                },
                onRotate: (deg) => {
                  const next = ((parseFloat(selectedWall.wall_rotation_y) || 0) + deg + 360) % 360;
                  const updated = { ...selectedWall, wall_rotation_y: next };
                  setSelectedWall(updated);
                  if (onUpdateWall) onUpdateWall(updated);
                },
                onDeselect: () => setSelectedWall(null)
              });
            })()}

            {/* Floating D-pad overlay for the currently selected structure */}
            {!placementMode && selectedStructure && multiSelected.length === 0 && (() => {
              const cx = parseFloat(selectedStructure.position_x) || 0;
              const cz = parseFloat(selectedStructure.position_z) || 0;
              const w = parseFloat(selectedStructure.width) || 12;
              const d = parseFloat(selectedStructure.depth) || 12;
              const rotY = parseFloat(selectedStructure.rotation_y) || 0;
              const rad = (rotY * Math.PI) / 180;
              const c = Math.abs(Math.cos(rad));
              const s = Math.abs(Math.sin(rad));
              const aabbW = (w * c + d * s) * finalScale;
              const aabbH = (w * s + d * c) * finalScale;
              const center = galleryToCanvas(cx, cz);
              return renderFloatingDPad({
                keyId: `structure-${selectedStructure.element_id}`,
                bbox: { topLeftX: center.x - aabbW / 2, topLeftY: center.y - aabbH / 2, widthPx: aabbW, heightPx: aabbH },
                label: selectedStructure.source || 'Structure',
                onMove: (dx, dz) => {
                  const updated = {
                    ...selectedStructure,
                    position_x: (parseFloat(selectedStructure.position_x) || 0) + dx,
                    position_z: (parseFloat(selectedStructure.position_z) || 0) + dz
                  };
                  setSelectedStructure(updated);
                  if (onUpdateStructure) onUpdateStructure(updated);
                },
                onRotate: (deg) => {
                  const next = ((parseFloat(selectedStructure.rotation_y) || 0) + deg + 360) % 360;
                  const updated = { ...selectedStructure, rotation_y: next };
                  setSelectedStructure(updated);
                  if (onUpdateStructure) onUpdateStructure(updated);
                },
                onDeselect: () => setSelectedStructure(null)
              });
            })()}
          </div>
          
          {/* Wall / Easel Details Panel — only show when something is selected AND not in placement mode */}
          {!placementMode && !isDetailsPanelCollapsed && (selectedWall || selectedEasel || selectedStructure || selectedPod) && (
            <div className="wall-details-panel">
              <h3>{selectedPod ? selectedPod : selectedWall ? selectedWall.source : selectedEasel ? (selectedEasel.source || 'Easel') : selectedStructure ? (selectedStructure.source || 'Structure') : 'Details'}</h3>
              {selectedPod && podGroups[selectedPod] ? (
                <>
                  <div className="position-inputs">
                    {(() => {
                      const pg = podGroups[selectedPod];
                      const firstWall = pg.walls[0];
                      if (!firstWall) return null;
                      const meta = (() => { try { return firstWall.text_content ? JSON.parse(firstWall.text_content) : {}; } catch { return {}; } })();
                      const isLegacy = meta.template === 'l-shape' || pg.walls.length > 1;
                      if (isLegacy) {
                        // Legacy L-shape — read-only info only
                        return (
                          <>
                            <div className="input-group">
                              <label>Type:</label>
                              <span>L-Shape (legacy) — {pg.walls.length} walls</span>
                            </div>
                            <div className="input-group">
                              <label>Dimensions:</label>
                              <span>{parseFloat(firstWall.width) || 96}" wide × {parseFloat(firstWall.height) || 96}" tall</span>
                            </div>
                          </>
                        );
                      }
                      // Tent-panel pod — editable controls
                      const tiltDeg = meta.tilt_angle || 70;
                      const wallW = parseFloat(firstWall.width) || 96;
                      const wallH = parseFloat(firstWall.height) || 96;
                      const tiltRad = (tiltDeg * Math.PI) / 180;
                      const floorDepth = (wallH * Math.cos(tiltRad)).toFixed(1);

                      const handlePodFieldChange = async (field, rawValue) => {
                        let value = parseFloat(rawValue);
                        if (isNaN(value)) return;
                        // Clamp values
                        if (field === 'tilt_angle') value = Math.min(90, Math.max(10, value));
                        if (field === 'width') value = Math.min(600, Math.max(12, value));
                        if (field === 'height') value = Math.min(240, Math.max(12, value));

                        const updatedMeta = { ...meta };
                        let newWidth = wallW;
                        let newHeight = wallH;
                        if (field === 'tilt_angle') updatedMeta.tilt_angle = value;
                        if (field === 'width') { updatedMeta.wallWidth = value; newWidth = value; }
                        if (field === 'height') { updatedMeta.wallHeight = value; newHeight = value; }

                        const newTilt = updatedMeta.tilt_angle || 70;
                        const newTiltRad = (newTilt * Math.PI) / 180;
                        const newCenterY = (newHeight * Math.sin(newTiltRad)) / 2;

                        await onUpdateWall({
                          ...firstWall,
                          width: newWidth,
                          height: newHeight,
                          wall_center_y: newCenterY,
                          text_content: JSON.stringify(updatedMeta)
                        });
                      };

                      return (
                        <>
                          <div className="input-group">
                            <label>Type:</label>
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>Tent Panel · {floorDepth}" floor depth</span>
                          </div>
                          <div className="input-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                            <div className="input-group">
                              <label>Tilt °:</label>
                              <input
                                type="number"
                                min={10} max={90} step={1}
                                defaultValue={tiltDeg}
                                onBlur={(e) => handlePodFieldChange('tilt_angle', e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                key={`tilt-${firstWall.element_id}-${tiltDeg}`}
                              />
                            </div>
                            <div className="input-group">
                              <label>Width "</label>
                              <input
                                type="number"
                                min={12} max={600} step={1}
                                defaultValue={wallW}
                                onBlur={(e) => handlePodFieldChange('width', e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                key={`width-${firstWall.element_id}-${wallW}`}
                              />
                            </div>
                            <div className="input-group">
                              <label>Height "</label>
                              <input
                                type="number"
                                min={12} max={240} step={1}
                                defaultValue={wallH}
                                onBlur={(e) => handlePodFieldChange('height', e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                key={`height-${firstWall.element_id}-${wallH}`}
                              />
                            </div>
                          </div>
                        </>
                      );
                    })()}
                    {/* Position controls — X / Z translate the pod, Y sets center height */}
                    {(() => {
                      const pg = podGroups[selectedPod];
                      if (!pg || !pg.walls.length) return null;
                      const bbox = getPodBoundingBox(pg.walls);
                      const firstWall = pg.walls[0];
                      const centerY = parseFloat(firstWall.wall_center_y) || 0;
                      const rotationY = parseFloat(firstWall.wall_rotation_y) || 0;
                      const valueRow = (axis, label, value) => (
                        <div className="input-group" style={{ marginBottom: '4px' }}>
                          <label style={{ minWidth: '100px' }}>{label}:</label>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input
                              type="number"
                              defaultValue={value.toFixed(1)}
                              step="1"
                              onBlur={(e) => {
                                if (axis === 'y') handleMovePod(selectedPod, { dy: parseFloat(e.target.value) || 0 });
                                else handleSetPodCenter(selectedPod, axis, e.target.value);
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              key={`pod-${axis}-${selectedPod}-${value.toFixed(1)}`}
                              style={{ width: '70px' }}
                            />
                            <span style={{ fontSize: '11px', color: '#6b7280' }}>"</span>
                          </div>
                        </div>
                      );
                      return (
                        <>
                          {valueRow('x', 'Left/Right', bbox.centerX)}
                          {valueRow('z', 'Up/Down', bbox.centerZ)}
                          {valueRow('y', 'Center Height', centerY)}
                          <div className="input-group" style={{ marginBottom: '4px' }}>
                            <label style={{ minWidth: '100px' }}>Rotation Y:</label>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <input
                                type="number"
                                defaultValue={rotationY.toFixed(1)}
                                step="15"
                                onBlur={(e) => {
                                  const next = parseFloat(e.target.value);
                                  if (isNaN(next)) return;
                                  const delta = next - rotationY;
                                  if (delta !== 0) handleRotatePod(selectedPod, delta);
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                key={`pod-rot-${selectedPod}-${rotationY.toFixed(1)}`}
                                style={{ width: '70px' }}
                              />
                              <span style={{ fontSize: '11px', color: '#6b7280' }}>°</span>
                            </div>
                          </div>
                          <small style={{ color: '#6b7280', marginTop: '-2px' }}>Use the floating arrows on the canvas to nudge position; tap the center ⟲ to rotate −15°.</small>
                        </>
                      );
                    })()}
                  </div>
                  <div className="input-group">
                    <label>Rename Pod:</label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input
                        type="text"
                        value={podRenameValue ?? selectedPod}
                        onChange={(e) => setPodRenameValue(e.target.value)}
                        placeholder="New pod name"
                        style={{ flex: 1 }}
                      />
                      <button
                        onClick={async () => {
                          const newName = (podRenameValue ?? '').trim();
                          if (!newName || newName === selectedPod) return;
                          if (podGroups[newName]) {
                            alert(`A pod named "${newName}" already exists. Please choose a different name.`);
                            return;
                          }
                          const podGroup = podGroups[selectedPod];
                          if (!podGroup) return;
                          for (const wall of podGroup.walls) {
                            const meta = JSON.parse(wall.text_content || '{}');
                            const oldSuffix = wall.source.replace(selectedPod, '');
                            meta.pod_group = newName;
                            await onUpdateWall({
                              ...wall,
                              source: newName + oldSuffix,
                              text_content: JSON.stringify(meta)
                            });
                          }
                          setSelectedPod(newName);
                          setPodRenameValue(null);
                        }}
                        style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #8b5cf6', background: '#f5f3ff', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                      >Rename</button>
                    </div>
                  </div>
                  {/* Artwork assigned to this pod */}
                  {(() => {
                    const allArt = (podGroups[selectedPod]?.walls || []).flatMap(w => {
                      const fresh = (sceneData?.walls || []).find(fw => fw.element_id === w.element_id) || w;
                      return (fresh.artwork || []).map(a => ({ ...a, wallName: fresh.source }));
                    });
                    return (
                      <div className="assigned-artwork-list">
                        <label>Artwork ({allArt.length}):</label>
                        {allArt.length === 0 ? (
                          <div className="no-artwork-msg">No artwork placed on this pod</div>
                        ) : (
                          <ul>{allArt.map(a => (
                            <li key={a.element_id}>
                              <img className="art-thumb" src={getImgUrl(a, 'thumbnail')} alt="" draggable={false} />
                              <div className="art-info">
                                <span className="art-title">{a.title || 'Untitled'}</span>
                                {a.artist_name && <span className="art-artist">{a.artist_name}</span>}
                                <span className="art-meta"><span className="art-dims">{a.width}"×{a.height}"</span> <span className="art-wall-name">on {a.wallName}</span></span>
                              </div>
                            </li>
                          ))}</ul>
                        )}
                      </div>
                    );
                  })()}
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '8px' }}>Drag outline to move. Tent panel tilts toward the ridge in 3D. Place two pods facing each other for an A-frame tent.</div>
                  {onDeletePod && (
                    <button
                      className="delete-element-btn"
                      onClick={() => { onDeletePod(selectedPod); setSelectedPod(null); }}
                    >
                      Delete Pod
                    </button>
                  )}
                </>
              ) : selectedEasel ? (
                <>
                  <div className="position-inputs">
                    <div className="input-group">
                      <label>Name:</label>
                      <input
                        type="text"
                        value={selectedEasel.source || ''}
                        onChange={(e) => setSelectedEasel({
                          ...selectedEasel,
                          source: e.target.value
                        })}
                        placeholder="Easel name"
                      />
                    </div>
                    <div className="input-group">
                      <label>Easel Group:</label>
                      <input
                        type="text"
                        value={(() => {
                          try {
                            const meta = selectedEasel.text_content ? JSON.parse(selectedEasel.text_content) : {};
                            return meta.easel_group || '';
                          } catch { return ''; }
                        })()}
                        onChange={(e) => {
                          const val = e.target.value;
                          let meta = {};
                          try { meta = selectedEasel.text_content ? JSON.parse(selectedEasel.text_content) : {}; } catch { /* ok */ }
                          if (val.trim()) {
                            meta.easel_group = val.trim();
                          } else {
                            delete meta.easel_group;
                          }
                          setSelectedEasel({
                            ...selectedEasel,
                            text_content: Object.keys(meta).length > 0 ? JSON.stringify(meta) : null
                          });
                        }}
                        placeholder="e.g., Row A, Entry Group"
                        list="easel-group-suggestions"
                      />
                      <datalist id="easel-group-suggestions">
                        {Object.keys(easelGroups).map(g => (
                          <option key={g} value={g} />
                        ))}
                      </datalist>
                      <span style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                        Assign to a group for the Easel Gallery strip & PDF
                      </span>
                    </div>
                    <div className="input-row">
                      <div className="input-group">
                        <label>Position X:</label>
                        <input
                          type="number"
                          value={selectedEasel.position_x || 0}
                          onChange={(e) => setSelectedEasel({
                            ...selectedEasel,
                            position_x: parseFloat(e.target.value) || 0
                          })}
                          step="1"
                        />
                      </div>
                      <div className="input-group">
                        <label>Position Z:</label>
                        <input
                          type="number"
                          value={selectedEasel.position_z || 0}
                          onChange={(e) => setSelectedEasel({
                            ...selectedEasel,
                            position_z: parseFloat(e.target.value) || 0
                          })}
                          step="1"
                        />
                      </div>
                    </div>
                    <div className="input-group">
                      <label>Rotation Y:</label>
                      <input
                        type="number"
                        value={selectedEasel.rotation_y || 0}
                        onChange={(e) => setSelectedEasel({
                          ...selectedEasel,
                          rotation_y: parseFloat(e.target.value) || 0
                        })}
                        step="15"
                        min="0"
                        max="360"
                      />
                    </div>
                    <div className="input-group">
                      <label>Center Height (inches):</label>
                      <input
                        type="number"
                        value={selectedEasel.position_y || 60}
                        onChange={(e) => setSelectedEasel({
                          ...selectedEasel,
                          position_y: parseFloat(e.target.value) || 60
                        })}
                        step="1"
                        min="24"
                        max="96"
                      />
                      <span style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                        Artwork centerline from floor (default 60" = eye level)
                      </span>
                    </div>
                  </div>
                  {/* Artwork assigned to this easel */}
                  {(() => {
                    const freshEasel = (sceneData?.easels || []).find(e => e.element_id === selectedEasel.element_id) || selectedEasel;
                    const artList = freshEasel.artwork || [];
                    return (
                      <div className="assigned-artwork-list">
                        <label>Artwork ({artList.length}):</label>
                        {artList.length === 0 ? (
                          <div className="no-artwork-msg">No artwork on this easel</div>
                        ) : (
                          <ul>{artList.map(a => (
                            <li key={a.element_id}>
                              <img className="art-thumb" src={getImgUrl(a, 'thumbnail')} alt="" draggable={false} />
                              <div className="art-info">
                                <span className="art-title">{a.title || 'Untitled'}</span>
                                {a.artist_name && <span className="art-artist">{a.artist_name}</span>}
                                <span className="art-dims">{a.width}"×{a.height}"</span>
                              </div>
                            </li>
                          ))}</ul>
                        )}
                      </div>
                    );
                  })()}
                  <div className="position-editor-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="save-btn"
                      onClick={() => onUpdateEasel && onUpdateEasel(selectedEasel)}
                    >
                      <Save size={16} />
                      Save Easel
                    </button>
                    {onDeleteEasel && (
                      <button
                        className="delete-element-btn"
                        onClick={() => { onDeleteEasel(selectedEasel.element_id); setSelectedEasel(null); }}
                      >
                        Delete Easel
                      </button>
                    )}
                  </div>
                </>
              ) : selectedStructure ? (
                <>
                  <div className="position-inputs">
                    <div className="input-group">
                      <label>Name:</label>
                      <input
                        type="text"
                        value={selectedStructure.source || ''}
                        onChange={(e) => setSelectedStructure({
                          ...selectedStructure,
                          source: e.target.value
                        })}
                        placeholder="Structure name"
                      />
                    </div>
                    <div className="input-group">
                      <label>Shape:</label>
                      <select
                        value={(() => { try { return JSON.parse(selectedStructure.text_content || '{}').shape || 'column-square'; } catch { return 'column-square'; } })()}
                        onChange={(e) => {
                          const shape = e.target.value;
                          let w = parseFloat(selectedStructure.width) || 12;
                          let d = parseFloat(selectedStructure.depth) || 12;
                          if (shape === 'column-square') { w = 12; d = 12; }
                          else if (shape === 'column-round') { w = 12; d = 12; }
                          else if (shape === 'column-rect') { w = 18; d = 12; }
                          else if (shape === 'wall-solid') { w = 120; d = 6; }
                          setSelectedStructure({
                            ...selectedStructure,
                            text_content: JSON.stringify({ shape }),
                            width: w,
                            depth: d
                          });
                        }}
                      >
                        <option value="column-square">Square Column</option>
                        <option value="column-round">Round Column</option>
                        <option value="column-rect">Rectangular Column</option>
                        <option value="wall-solid">Solid Wall</option>
                      </select>
                    </div>
                    <div className="input-row">
                      <div className="input-group">
                        <label>Width (in):</label>
                        <input
                          type="number"
                          value={selectedStructure.width || 12}
                          onChange={(e) => setSelectedStructure({
                            ...selectedStructure,
                            width: parseFloat(e.target.value) || 12
                          })}
                          step="1"
                          min="1"
                        />
                        <span style={{ fontSize: '11px', color: '#666' }}>{((parseFloat(selectedStructure.width) || 12) / 12).toFixed(1)} ft</span>
                      </div>
                      <div className="input-group">
                        <label>Depth (in):</label>
                        <input
                          type="number"
                          value={selectedStructure.depth || 12}
                          onChange={(e) => setSelectedStructure({
                            ...selectedStructure,
                            depth: parseFloat(e.target.value) || 12
                          })}
                          step="1"
                          min="1"
                        />
                        <span style={{ fontSize: '11px', color: '#666' }}>{((parseFloat(selectedStructure.depth) || 12) / 12).toFixed(1)} ft</span>
                      </div>
                    </div>
                    <div className="input-group">
                      <label>Height (in):</label>
                      <input
                        type="number"
                        value={selectedStructure.height || 96}
                        onChange={(e) => setSelectedStructure({
                          ...selectedStructure,
                          height: parseFloat(e.target.value) || 96
                        })}
                        step="1"
                        min="1"
                      />
                      <span style={{ fontSize: '11px', color: '#666' }}>{((parseFloat(selectedStructure.height) || 96) / 12).toFixed(1)} ft</span>
                    </div>
                    <div className="input-row">
                      <div className="input-group">
                        <label>Position X:</label>
                        <input
                          type="number"
                          value={selectedStructure.position_x || 0}
                          onChange={(e) => setSelectedStructure({
                            ...selectedStructure,
                            position_x: parseFloat(e.target.value) || 0
                          })}
                          step="1"
                        />
                      </div>
                      <div className="input-group">
                        <label>Position Z:</label>
                        <input
                          type="number"
                          value={selectedStructure.position_z || 0}
                          onChange={(e) => setSelectedStructure({
                            ...selectedStructure,
                            position_z: parseFloat(e.target.value) || 0
                          })}
                          step="1"
                        />
                      </div>
                    </div>
                    <div className="input-group">
                      <label>Rotation Y:</label>
                      <input
                        type="number"
                        value={selectedStructure.rotation_y || 0}
                        onChange={(e) => setSelectedStructure({
                          ...selectedStructure,
                          rotation_y: parseFloat(e.target.value) || 0
                        })}
                        step="15"
                        min="0"
                        max="360"
                      />
                    </div>
                    <div className="input-group">
                      <label>Color:</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="color"
                          value={selectedStructure.color || '#9ca3af'}
                          onChange={(e) => setSelectedStructure({
                            ...selectedStructure,
                            color: e.target.value
                          })}
                          className="color-picker"
                        />
                        <span style={{ fontSize: '11px', color: '#666' }}>{selectedStructure.color || '#9ca3af'}</span>
                      </div>
                    </div>
                    <div className="input-group">
                      <label>Opacity:</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={(() => { try { const m = selectedStructure.text_content ? JSON.parse(selectedStructure.text_content) : {}; return m.opacity ?? 1; } catch { return 1; } })()}
                          onChange={(e) => {
                            const newOpacity = parseFloat(e.target.value);
                            let meta;
                            try { meta = selectedStructure.text_content ? JSON.parse(selectedStructure.text_content) : {}; } catch { meta = {}; }
                            meta.opacity = newOpacity;
                            setSelectedStructure({ ...selectedStructure, text_content: JSON.stringify(meta) });
                          }}
                          style={{ flex: 1 }}
                        />
                        <span style={{ fontSize: '12px', color: '#666', minWidth: '36px' }}>
                          {(() => { try { const m = selectedStructure.text_content ? JSON.parse(selectedStructure.text_content) : {}; return Math.round((m.opacity ?? 1) * 100); } catch { return 100; } })()}%
                        </span>
                      </div>
                      <small>Reduce to see through this structure in 3D</small>
                    </div>
                  </div>
                  <div className="position-editor-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="save-btn"
                      onClick={() => {
                        if (onUpdateStructure) {
                          let meta;
                          try { meta = JSON.parse(selectedStructure.text_content || '{}'); } catch { meta = {}; }
                          onUpdateStructure({
                            ...selectedStructure,
                            _fullUpdate: true,
                            shape: meta.shape || 'column-square'
                          });
                        }
                      }}
                    >
                      <Save size={16} />
                      Save Changes
                    </button>
                    {onDeleteStructure && (
                      <button
                        className="delete-element-btn"
                        onClick={() => { onDeleteStructure(selectedStructure.element_id); setSelectedStructure(null); }}
                      >
                        Delete Structure
                      </button>
                    )}
                  </div>
                </>
              ) : selectedWall ? (
                <>
                  <div className="position-inputs">
                    <div className="input-group">
                      <label>Length / Height:</label>
                      <span>{(parseFloat(selectedWall.width) || 0).toFixed(1)}" × {(parseFloat(selectedWall.height) || 0).toFixed(1)}"</span>
                    </div>

                    <div className="edge-metrics">
                      <div className="metric-chip">
                        <span>Left Edge (X)</span>
                        <strong>{((parseFloat(selectedWall.wall_center_x) || 0) - ((parseFloat(selectedWall.width) || 0) / 2)).toFixed(2)}"</strong>
                      </div>
                      <div className="metric-chip">
                        <span>Right Edge (X)</span>
                        <strong>{((parseFloat(selectedWall.wall_center_x) || 0) + ((parseFloat(selectedWall.width) || 0) / 2)).toFixed(2)}"</strong>
                      </div>
                    </div>

                    <div className="input-row">
                      <div className="input-group">
                        <label>Center X:</label>
                        <input
                          type="number"
                          value={selectedWall.wall_center_x || 0}
                          onChange={(e) => setSelectedWall({
                            ...selectedWall,
                            wall_center_x: parseFloat(e.target.value) || 0
                          })}
                          step="1"
                        />
                      </div>
                      <div className="input-group">
                        <label>Center Z:</label>
                        <input
                          type="number"
                          value={selectedWall.wall_center_z || 0}
                          onChange={(e) => setSelectedWall({
                            ...selectedWall,
                            wall_center_z: parseFloat(e.target.value) || 0
                          })}
                          step="1"
                        />
                      </div>
                    </div>

                    <div className="input-row">
                      <div className="input-group">
                        <label>Center Y:</label>
                        <input
                          type="number"
                          value={selectedWall.wall_center_y || 72}
                          onChange={(e) => setSelectedWall({
                            ...selectedWall,
                            wall_center_y: parseFloat(e.target.value) || 72
                          })}
                          step="1"
                        />
                      </div>
                      <div className="input-group">
                        <label>Rotation Y:</label>
                        <input
                          type="number"
                          value={selectedWall.wall_rotation_y || 0}
                          onChange={(e) => setSelectedWall({
                            ...selectedWall,
                            wall_rotation_y: parseFloat(e.target.value) || 0
                          })}
                          step="90"
                          min="0"
                          max="360"
                        />
                      </div>
                    </div>
                    <small style={{color: '#6b7280', marginTop: '-2px'}}>
                      2D preview shows: {(-parseFloat(selectedWall.wall_rotation_y || 0)).toFixed(1)}°
                    </small>

                    <div className="input-group">
                      <label>Opacity:</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={(() => { try { const m = selectedWall.text_content ? JSON.parse(selectedWall.text_content) : {}; return m.opacity ?? 1; } catch { return 1; } })()}
                          onChange={(e) => {
                            const newOpacity = parseFloat(e.target.value);
                            let meta;
                            try { meta = selectedWall.text_content ? JSON.parse(selectedWall.text_content) : {}; } catch { meta = {}; }
                            meta.opacity = newOpacity;
                            setSelectedWall({ ...selectedWall, text_content: JSON.stringify(meta) });
                          }}
                          style={{ flex: 1 }}
                        />
                        <span style={{ fontSize: '12px', color: '#666', minWidth: '36px' }}>
                          {(() => { try { const m = selectedWall.text_content ? JSON.parse(selectedWall.text_content) : {}; return Math.round((m.opacity ?? 1) * 100); } catch { return 100; } })()}%
                        </span>
                      </div>
                      <small>Reduce to see behind this wall in 3D</small>
                    </div>

                    <div className="input-group">
                      <label>Wall Material:</label>
                      <div className="wall-material-section">
                        <div className="material-dropdown-section">
                          <select
                            value={selectedWall.color || 'drywall'}
                            onChange={(e) => handleWallMaterialChange(e.target.value)}
                          >
                            {Object.entries(wallMaterialPresets).map(([key, preset]) => (
                              <option key={key} value={key}>
                                {preset.texture} {preset.name}
                              </option>
                            ))}
                          </select>
                          <small>
                            {wallMaterialPresets[selectedWall.color || 'drywall']?.description}
                          </small>
                        </div>

                        <div className="custom-color-section">
                          <label>Custom Color:</label>
                          <div className="color-picker-group">
                            <input
                              type="color"
                              value={selectedWall.background_color || wallMaterialPresets[selectedWall.color || 'drywall'].color}
                              onChange={(e) => setSelectedWall({
                                ...selectedWall,
                                background_color: e.target.value
                              })}
                              className="color-picker"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Artwork assigned to this wall */}
                  {(() => {
                    const freshWall = (sceneData?.walls || []).find(w => w.element_id === selectedWall.element_id) || selectedWall;
                    const artList = freshWall.artwork || [];
                    return (
                      <div className="assigned-artwork-list">
                        <label>Artwork ({artList.length}):</label>
                        {artList.length === 0 ? (
                          <div className="no-artwork-msg">No artwork placed on this wall</div>
                        ) : (
                          <ul>{artList.map(a => (
                            <li key={a.element_id}>
                              <img className="art-thumb" src={getImgUrl(a, 'thumbnail')} alt="" draggable={false} />
                              <div className="art-info">
                                <span className="art-title">{a.title || 'Untitled'}</span>
                                {a.artist_name && <span className="art-artist">{a.artist_name}</span>}
                                <span className="art-dims">{a.width}"×{a.height}"</span>
                              </div>
                            </li>
                          ))}</ul>
                        )}
                      </div>
                    );
                  })()}

                  <div className="position-editor-actions" style={{ marginTop: '12px' }}>
                    <button
                      className="save-btn"
                      onClick={() => handleSavePosition(selectedWall)}
                    >
                      <Save size={16} />
                      Save Position
                    </button>
                    <button
                      className="refresh-btn square-btn"
                      onClick={refreshEditingWall}
                      title="Refresh with current wall position"
                    >
                      <span className="icon-text">↻</span>
                    </button>
                    <button
                      className="cancel-btn"
                      onClick={handleCancelEdit}
                    >
                      Revert
                    </button>
                    {onDeleteWall && (
                      <button
                        className="delete-element-btn"
                        onClick={() => { onDeleteWall(selectedWall.element_id); setSelectedWall(null); }}
                      >
                        Delete Wall
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="no-selection">
                  <p>Click on a wall to see details</p>
                  <p>Edit selected wall position directly in this panel</p>
                  <p>Drag walls to move them</p>
                </div>
              )}
            </div>
          )}

          {/* Layout mode — inventory preview panel (read-only browsing) */}
          {!placementMode && showLayoutInventory && inventoryData && (
            <div className="layout-inventory-panel">
              <div className="layout-inventory-header">
                <span className="layout-inventory-title">Artwork Inventory</span>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {(inventoryData?.accepted?.length > 0 || inventoryData?.pending?.length > 0) && (
                    <>
                      <button
                        onClick={() => setInventoryTab('accepted')}
                        className={`inventory-tab-btn ${inventoryTab === 'accepted' ? 'active-accepted' : ''}`}
                      >
                        ✓ Accepted ({inventoryData?.totalAccepted || 0})
                      </button>
                      <button
                        onClick={() => setInventoryTab('pending')}
                        className={`inventory-tab-btn ${inventoryTab === 'pending' ? 'active-pending' : ''}`}
                      >
                        ? Pending ({inventoryData?.totalPending || 0})
                      </button>
                    </>
                  )}
                  <button onClick={() => setShowLayoutInventory(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: '#6b7280' }} title="Close inventory">
                    <X size={16} />
                  </button>
                </div>
              </div>
              <input
                type="text"
                className="inventory-dock-search"
                placeholder="Search by title or artist..."
                value={inventorySearch}
                onChange={(e) => setInventorySearch(e.target.value)}
              />
              <div className="layout-inventory-items">
                {filteredUnplaced.length === 0 ? (
                  <div className="inventory-dock-empty">No unplaced artworks</div>
                ) : (
                  filteredUnplaced.map(art => (
                    <div key={art.vc_id} className={`layout-inventory-item ${art.accepted === false ? 'inventory-dock-item-pending' : ''}`}>
                      <div className="inventory-dock-thumb-wrapper">
                        <img src={getImgUrl(art, 'thumbnail')} alt={art.title || ''} loading="lazy" />
                        {art.accepted === true && <span className="inventory-dock-badge badge-accepted">✓</span>}
                        {art.accepted === false && <span className="inventory-dock-badge badge-pending">?</span>}
                      </div>
                      <div className="inventory-dock-item-info">
                        <span className="inventory-dock-item-title">{art.title || 'Untitled'}</span>
                        <span className="inventory-dock-item-artist">{art.artist_name || ''}</span>
                        <span className="inventory-dock-item-dims">{art.width || '?'}" × {art.height || '?'}"</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          </div>{/* end planner-main-row */}

          {/* ─── Easel Gallery Strip (horizontal, grouped) ─── */}
          {!placementMode && showEaselGallery && (easels || []).length > 0 && (
            <div className="easel-gallery-strip">
              <div className="easel-gallery-strip-header">
                <span className="easel-gallery-strip-title">🖼 Easel Gallery</span>
                <span className="easel-gallery-strip-count">{(easels || []).length} easel{(easels || []).length !== 1 ? 's' : ''}</span>
                <button onClick={() => setShowEaselGallery(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: '#6b7280', marginLeft: 'auto' }} title="Close easel gallery">
                  <X size={16} />
                </button>
              </div>
              <div className="easel-gallery-strip-body">
                {Object.entries(easelGroups).sort(([a], [b]) => a.localeCompare(b)).map(([groupName, group]) => (
                  <div key={`eg-${groupName}`} className="easel-gallery-group">
                    <div className="easel-gallery-group-label">{groupName} <span className="easel-gallery-group-count">({group.easels.length})</span></div>
                    <div className="easel-gallery-group-items">
                      {group.easels.map(easel => {
                        const sd = (sceneData?.easels || []).find(se => se.element_id === easel.element_id);
                        const art = sd?.artwork?.[0] || null;
                        const isSelected = selectedEasel?.element_id === easel.element_id;
                        return (
                          <button
                            key={easel.element_id}
                            className={`easel-gallery-card ${isSelected ? 'selected' : ''} ${art ? 'has-art' : ''}`}
                            onClick={() => { setSelectedEasel(easel); setSelectedWall(null); setSelectedPod(null); setSelectedStructure(null); setMultiSelected([]); }}
                          >
                            <div className="easel-gallery-card-img">
                              {art ? (
                                <img src={getImgUrl(art, 'thumbnail')} alt={art.title || ''} loading="lazy" />
                              ) : (
                                <span className="easel-gallery-empty-icon">🖼</span>
                              )}
                            </div>
                            <div className="easel-gallery-card-info">
                              <span className="easel-gallery-card-name">{easel.source || 'Unnamed'}</span>
                              {art ? (
                                <>
                                  <span className="easel-gallery-card-title">{art.title || 'Untitled'}</span>
                                  <span className="easel-gallery-card-artist">{art.artist_name || ''}</span>
                                  {art.width && art.height && <span className="easel-gallery-card-dims">{art.width}" × {art.height}"</span>}
                                </>
                              ) : (
                                <span className="easel-gallery-card-empty">Empty</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {ungroupedEasels.length > 0 && (
                  <div className="easel-gallery-group ungrouped">
                    <div className="easel-gallery-group-label">{Object.keys(easelGroups).length > 0 ? 'Ungrouped' : 'All Easels'} <span className="easel-gallery-group-count">({ungroupedEasels.length})</span></div>
                    <div className="easel-gallery-group-items">
                      {ungroupedEasels.map(easel => {
                        const sd = (sceneData?.easels || []).find(se => se.element_id === easel.element_id);
                        const art = sd?.artwork?.[0] || null;
                        const isSelected = selectedEasel?.element_id === easel.element_id;
                        return (
                          <button
                            key={easel.element_id}
                            className={`easel-gallery-card ${isSelected ? 'selected' : ''} ${art ? 'has-art' : ''}`}
                            onClick={() => { setSelectedEasel(easel); setSelectedWall(null); setSelectedPod(null); setSelectedStructure(null); setMultiSelected([]); }}
                          >
                            <div className="easel-gallery-card-img">
                              {art ? (
                                <img src={getImgUrl(art, 'thumbnail')} alt={art.title || ''} loading="lazy" />
                              ) : (
                                <span className="easel-gallery-empty-icon">🖼</span>
                              )}
                            </div>
                            <div className="easel-gallery-card-info">
                              <span className="easel-gallery-card-name">{easel.source || 'Unnamed'}</span>
                              {art ? (
                                <>
                                  <span className="easel-gallery-card-title">{art.title || 'Untitled'}</span>
                                  <span className="easel-gallery-card-artist">{art.artist_name || ''}</span>
                                  {art.width && art.height && <span className="easel-gallery-card-dims">{art.width}" × {art.height}"</span>}
                                </>
                              ) : (
                                <span className="easel-gallery-card-empty">Empty</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Art Placement: Full-screen Wall Surface Overlay ─── */}
          {placementMode && placementTarget && (
            <div className="art-placement-fullscreen">
              <div className="art-placement-fullscreen-header">
                <div className="art-placement-fullscreen-title">
                  <select
                    className="placement-target-select"
                    value={
                      placementTarget.type === 'pod' ? `pod:${placementTarget.podName}`
                        : placementTarget.type === 'wall' ? `wall:${placementTarget.element.element_id}`
                        : `easel:${placementTarget.element.element_id}`
                    }
                    onChange={(e) => handleSwitchPlacementTarget(e.target.value)}
                  >
                    {allPlacementTargets.filter(t => t.type === 'pod').length > 0 && (
                      <optgroup label="Pods">
                        {allPlacementTargets.filter(t => t.type === 'pod').sort((a, b) => a.label.localeCompare(b.label)).map(t => (
                          <option key={t.key} value={t.key}>
                            {t.hasArt ? '✅ ' : '⬜ '}{t.label} ({t.artCount} art)
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {allPlacementTargets.filter(t => t.type === 'wall').length > 0 && (
                      <optgroup label="Walls">
                        {allPlacementTargets.filter(t => t.type === 'wall').sort((a, b) => a.label.localeCompare(b.label)).map(t => (
                          <option key={t.key} value={t.key}>
                            {t.hasArt ? '✅ ' : '⬜ '}{t.label} ({t.artCount} art)
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {allPlacementTargets.filter(t => t.type === 'easel').length > 0 && (
                      <optgroup label="Easels">
                        {allPlacementTargets.filter(t => t.type === 'easel').sort((a, b) => a.label.localeCompare(b.label)).map(t => (
                          <option key={t.key} value={t.key}>
                            {t.hasArt ? '✅ ' : '⬜ '}{t.label} ({t.artCount} art)
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {(placementTarget.type === 'pod' || placementTarget.type === 'easel') && !placementRenaming && (
                    <button
                      className="placement-rename-btn"
                      onClick={() => {
                        setPlacementRenaming(true);
                        setPlacementRenameValue(
                          placementTarget.type === 'pod' ? placementTarget.podName : (placementTarget.element?.source || 'Easel')
                        );
                      }}
                      title={`Rename ${placementTarget.type}`}
                    >
                      <Edit2 size={14} />
                    </button>
                  )}
                  {(placementTarget.type === 'pod' || placementTarget.type === 'easel') && placementRenaming && (
                    <div className="placement-rename-inline">
                      <input
                        type="text"
                        value={placementRenameValue}
                        onChange={(e) => setPlacementRenameValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.nextSibling.click(); if (e.key === 'Escape') setPlacementRenaming(false); }}
                        placeholder={placementTarget.type === 'pod' ? 'New pod name' : 'New easel name'}
                        autoFocus
                      />
                      <button
                        className="placement-rename-save"
                        onClick={async () => {
                          const newName = placementRenameValue.trim();
                          if (!newName) { setPlacementRenaming(false); return; }
                          if (placementTarget.type === 'pod') {
                            if (newName === placementTarget.podName) { setPlacementRenaming(false); return; }
                            if (podGroups[newName]) {
                              alert(`A pod named "${newName}" already exists. Please choose a different name.`);
                              return;
                            }
                            const podGroup = podGroups[placementTarget.podName];
                            if (!podGroup) return;
                            for (const wall of podGroup.walls) {
                              const meta = JSON.parse(wall.text_content || '{}');
                              const oldSuffix = wall.source.replace(placementTarget.podName, '');
                              meta.pod_group = newName;
                              await onUpdateWall({ ...wall, source: newName + oldSuffix, text_content: JSON.stringify(meta) });
                            }
                            setSelectedPod(newName);
                            setPlacementTarget(prev => prev ? { ...prev, podName: newName } : null);
                          } else if (placementTarget.type === 'easel') {
                            if (newName === placementTarget.element?.source) { setPlacementRenaming(false); return; }
                            const updatedEasel = { ...placementTarget.element, source: newName };
                            if (onUpdateEasel) await onUpdateEasel(updatedEasel);
                            setSelectedEasel(updatedEasel);
                            setPlacementTarget(prev => prev ? { ...prev, element: updatedEasel } : null);
                          }
                          setPlacementRenaming(false);
                          setPodRenameValue(null);
                        }}
                      >
                        <Check size={14} />
                      </button>
                      <button className="placement-rename-cancel" onClick={() => setPlacementRenaming(false)}>
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  <span className="surface-panel-dims">
                    {placementTarget.type === 'wall' && `${placementTarget.element.width || 120}" wide × ${placementTarget.element.height || 96}" tall`}
                    {placementTarget.type === 'easel' && 'Easel — holds one artwork'}
                    {placementTarget.type === 'pod' && `Tent Panel — ${placementTarget.walls.length} surface${placementTarget.walls.length !== 1 ? 's' : ''}`}
                  </span>
                  <span className="surface-panel-count">
                    {targetArtworks.length} artwork{targetArtworks.length !== 1 ? 's' : ''} placed
                  </span>
                </div>
                <button className="art-placement-done-btn" onClick={() => setPlacementTarget(null)}>Done</button>
              </div>

              <div className="art-placement-fullscreen-body">

                {/* ── WALL type ── */}
                {placementTarget.type === 'wall' && (
                  <div className="art-placement-wall-container">
                    <div
                      className="surface-panel-wall"
                      style={{
                        width: `min(100cqi, calc(${(parseFloat(placementTarget.element.width || 120) / parseFloat(placementTarget.element.height || 96)).toFixed(6)} * 100cqb))`,
                        height: `min(100cqb, calc(${(parseFloat(placementTarget.element.height || 96) / parseFloat(placementTarget.element.width || 120)).toFixed(6)} * 100cqi))`
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={handleArtPlacementDrop}
                    >
                      <div className="surface-wall-dims-label">{parseFloat(placementTarget.element.width || 120)}" w × {parseFloat(placementTarget.element.height || 96)}" h</div>
                      {(() => {
                        const wallH = parseFloat(placementTarget.element.height || 96);
                        const eyeFromTop = wallH - 57;
                        if (eyeFromTop <= 0 || eyeFromTop >= wallH) return null;
                        return (
                          <div className="surface-eye-level" style={{ top: `${(eyeFromTop / wallH) * 100}%` }}>
                            <span>Eye Level 57"</span>
                          </div>
                        );
                      })()}
                      {targetArtworks.map(art => {
                        const wallW = parseFloat(placementTarget.element.width || 120);
                        const wallH = parseFloat(placementTarget.element.height || 96);
                        const leftPct = ((art.wall_position_x || 0) / wallW) * 100;
                        const topPct = ((art.wall_position_y || 0) / wallH) * 100;
                        const wPct = ((art.width || 24) / wallW) * 100;
                        const hPct = ((art.height || 24) / wallH) * 100;
                        const isSelected = selectedArtworkId === art.element_id;
                        return (
                          <div
                            key={art.element_id}
                            className={`surface-artwork ${isSelected ? 'surface-artwork-selected' : ''}`}
                            style={{ left: `${leftPct}%`, top: `${topPct}%`, width: `${wPct}%`, height: `${hPct}%` }}
                            draggable
                            onClick={(e) => { e.stopPropagation(); setSelectedArtworkId(art.element_id); }}
                            onDragStart={(e) => {
                              setDraggingArtworkItem({ ...art, isFromInventory: false });
                              setIsDraggingArtwork(true);
                              e.dataTransfer.setData('text/plain', art.element_id);
                              e.dataTransfer.effectAllowed = 'move';
                            }}
                            title={`${art.title || 'Artwork'} — ${art.width}"×${art.height}" — drag to reposition or click + arrow keys to nudge`}
                          >
                            <img src={getImgUrl(art, 'display')} alt={art.title || ''} draggable={false} />
                            <button
                              className="surface-artwork-remove"
                              onClick={(e) => { e.stopPropagation(); handleRemoveArtworkFromTarget(art.element_id); }}
                              title="Remove from wall"
                            >×</button>
                            {isSelected && (
                              <div className="surface-nudge-controls">
                                <button className="surface-nudge-btn nudge-up" onClick={(e) => { e.stopPropagation(); handleNudgeArtwork(art.element_id, placementTarget.element.element_id, 0, e.shiftKey ? -0.25 : -1); }} title={`Move up 1" (Shift = 0.25")`}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-8 8h5v8h6v-8h5z"/></svg></button>
                                <button className="surface-nudge-btn nudge-down" onClick={(e) => { e.stopPropagation(); handleNudgeArtwork(art.element_id, placementTarget.element.element_id, 0, e.shiftKey ? 0.25 : 1); }} title={`Move down 1" (Shift = 0.25")`}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 20l8-8h-5v-8h-6v8h-5z"/></svg></button>
                                <button className="surface-nudge-btn nudge-left" onClick={(e) => { e.stopPropagation(); handleNudgeArtwork(art.element_id, placementTarget.element.element_id, e.shiftKey ? -0.25 : -1, 0); }} title={`Move left 1" (Shift = 0.25")`}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M4 12l8-8v5h8v6h-8v5z"/></svg></button>
                                <button className="surface-nudge-btn nudge-right" onClick={(e) => { e.stopPropagation(); handleNudgeArtwork(art.element_id, placementTarget.element.element_id, e.shiftKey ? 0.25 : 1, 0); }} title={`Move right 1" (Shift = 0.25")`}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 12l-8 8v-5h-8v-6h8v-5z"/></svg></button>
                              </div>
                            )}
                            {isSelected && (
                              <div className="surface-artwork-dims-overlay">
                                <span className="artwork-dims-text">{art.width}"w × {art.height}"h</span>
                                <button className="artwork-swap-btn" onClick={(e) => { e.stopPropagation(); handleSwapArtworkDimensions(art); }} title="Swap width ↔ height">⇄ Swap W/H</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {targetArtworks.length === 0 && (
                        <div className="surface-drop-hint">Drag artwork here from the inventory below</div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── EASEL type — single artwork slot ── */}
                {placementTarget.type === 'easel' && (() => {
                  const easel = (sceneData?.easels || []).find(e => e.element_id === placementTarget.element.element_id);
                  const art = easel?.artwork?.[0] || null;
                  return (
                    <div className="art-placement-wall-container">
                      <div
                        className="surface-panel-wall easel-surface"
                        style={{
                          aspectRatio: art ? `${art.width || 24} / ${art.height || 24}` : '3 / 4',
                          maxWidth: '400px',
                          width: '100%',
                          height: 'auto',
                          maxHeight: '100%'
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleArtPlacementDrop}
                      >
                        {art ? (
                          <div className="surface-artwork" style={{ inset: 0, width: '100%', height: '100%' }}>
                            <img src={getImgUrl(art, 'display')} alt={art.title || ''} draggable={false} />
                            <button
                              className="surface-artwork-remove"
                              onClick={(e) => { e.stopPropagation(); handleRemoveArtworkFromTarget(art.element_id); }}
                              title="Remove from easel"
                            >×</button>
                            <div className="easel-artwork-label">{art.title || 'Artwork'} — {art.width}"×{art.height}"</div>
                          </div>
                        ) : (
                          <div className="surface-drop-hint">Drag artwork here to place on easel</div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* ── POD type — single wall uses wall-container layout; multi-wall side by side ── */}
                {placementTarget.type === 'pod' && (() => {
                  const podWalls = placementTarget.walls || [];
                  const isSingleWall = podWalls.length === 1;

                  if (isSingleWall) {
                    // Single-wall tent pod — render like a regular wall for correct aspect ratio
                    const podWall = podWalls[0];
                    const freshWall = (sceneData?.walls || []).find(w => w.element_id === podWall.element_id) || podWall;
                    const wallW = parseFloat(freshWall.width || 96);
                    const wallH = parseFloat(freshWall.height || 96);
                    const wallArt = freshWall.artwork || [];
                    return (
                      <div className="art-placement-wall-container">
                        <div
                          className="surface-panel-wall"
                          style={{
                            width: `min(100cqi, calc(${(wallW / wallH).toFixed(6)} * 100cqb))`,
                            height: `min(100cqb, calc(${(wallH / wallW).toFixed(6)} * 100cqi))`
                          }}
                          data-wall-id={freshWall.element_id}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => handlePodWallDrop(e, freshWall)}
                        >
                          <div className="surface-wall-dims-label">{wallW}" w × {wallH}" h</div>
                          {(() => {
                            const eyeFromTop = wallH - 57;
                            if (eyeFromTop <= 0 || eyeFromTop >= wallH) return null;
                            return (
                              <div className="surface-eye-level" style={{ top: `${(eyeFromTop / wallH) * 100}%` }}>
                                <span>Eye Level 57"</span>
                              </div>
                            );
                          })()}
                          {wallArt.map(art => {
                            const leftPct = ((art.wall_position_x || 0) / wallW) * 100;
                            const topPct = ((art.wall_position_y || 0) / wallH) * 100;
                            const wPct = ((art.width || 24) / wallW) * 100;
                            const hPct = ((art.height || 24) / wallH) * 100;
                            const isSelected = selectedArtworkId === art.element_id;
                            return (
                              <div
                                key={art.element_id}
                                className={`surface-artwork ${isSelected ? 'surface-artwork-selected' : ''}`}
                                style={{ left: `${leftPct}%`, top: `${topPct}%`, width: `${wPct}%`, height: `${hPct}%` }}
                                draggable
                                onClick={(e) => { e.stopPropagation(); setSelectedArtworkId(art.element_id); }}
                                onDragStart={(e) => {
                                  setDraggingArtworkItem({ ...art, isFromInventory: false });
                                  setIsDraggingArtwork(true);
                                  e.dataTransfer.setData('text/plain', art.element_id);
                                  e.dataTransfer.effectAllowed = 'move';
                                }}
                                title={`${art.title || 'Artwork'} — ${art.width}"×${art.height}" — drag or click + arrows`}
                              >
                                <img src={getImgUrl(art, 'display')} alt={art.title || ''} draggable={false} />
                                <button
                                  className="surface-artwork-remove"
                                  onClick={(ev) => { ev.stopPropagation(); handleRemoveArtworkFromTarget(art.element_id); }}
                                  title="Remove from wall"
                                >×</button>
                                {isSelected && (
                                  <div className="surface-nudge-controls">
                                    <button className="surface-nudge-btn nudge-up" onClick={(ev) => { ev.stopPropagation(); handleNudgeArtwork(art.element_id, freshWall.element_id, 0, ev.shiftKey ? -0.25 : -1); }} title={`Move up 1" (Shift = 0.25")`}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-8 8h5v8h6v-8h5z"/></svg></button>
                                    <button className="surface-nudge-btn nudge-down" onClick={(ev) => { ev.stopPropagation(); handleNudgeArtwork(art.element_id, freshWall.element_id, 0, ev.shiftKey ? 0.25 : 1); }} title={`Move down 1" (Shift = 0.25")`}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 20l8-8h-5v-8h-6v8h-5z"/></svg></button>
                                    <button className="surface-nudge-btn nudge-left" onClick={(ev) => { ev.stopPropagation(); handleNudgeArtwork(art.element_id, freshWall.element_id, ev.shiftKey ? -0.25 : -1, 0); }} title={`Move left 1" (Shift = 0.25")`}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M4 12l8-8v5h8v6h-8v5z"/></svg></button>
                                    <button className="surface-nudge-btn nudge-right" onClick={(ev) => { ev.stopPropagation(); handleNudgeArtwork(art.element_id, freshWall.element_id, ev.shiftKey ? 0.25 : 1, 0); }} title={`Move right 1" (Shift = 0.25")`}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 12l-8 8v-5h-8v-6h8v-5z"/></svg></button>
                                  </div>
                                )}
                                {isSelected && (
                                  <div className="surface-artwork-dims-overlay">
                                    <span className="artwork-dims-text">{art.width}"w × {art.height}"h</span>
                                    <button className="artwork-swap-btn" onClick={(ev) => { ev.stopPropagation(); handleSwapArtworkDimensions(art); }} title="Swap width ↔ height">⇄ Swap W/H</button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {wallArt.length === 0 && (
                            <div className="surface-drop-hint">Drag artwork here from the inventory below</div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Multi-wall pod — side-by-side layout (legacy)
                  return (
                    <div className="art-placement-pod-container">
                      {podWalls.map((podWall, idx) => {
                      const freshWall = (sceneData?.walls || []).find(w => w.element_id === podWall.element_id) || podWall;
                      const wallW = parseFloat(freshWall.width || 96);
                      const wallH = parseFloat(freshWall.height || 96);
                      const wallArt = freshWall.artwork || [];
                      return (
                        <div key={freshWall.element_id} className="pod-wall-column">
                          <div className="pod-wall-label">{freshWall.source || `Panel ${idx + 1}`} <span>({wallW}" × {wallH}")</span></div>
                          <div
                            className="surface-panel-wall"
                            style={{ aspectRatio: `${wallW} / ${wallH}` }}
                            data-wall-id={freshWall.element_id}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => handlePodWallDrop(e, freshWall)}
                          >
                            {(() => {
                              const eyeFromTop = wallH - 57;
                              if (eyeFromTop <= 0 || eyeFromTop >= wallH) return null;
                              return (
                                <div className="surface-eye-level" style={{ top: `${(eyeFromTop / wallH) * 100}%` }}>
                                  <span>57"</span>
                                </div>
                              );
                            })()}
                            {wallArt.map(art => {
                              const leftPct = ((art.wall_position_x || 0) / wallW) * 100;
                              const topPct = ((art.wall_position_y || 0) / wallH) * 100;
                              const wPct = ((art.width || 24) / wallW) * 100;
                              const hPct = ((art.height || 24) / wallH) * 100;
                              const isSelected = selectedArtworkId === art.element_id;
                              return (
                                <div
                                  key={art.element_id}
                                  className={`surface-artwork ${isSelected ? 'surface-artwork-selected' : ''}`}
                                  style={{ left: `${leftPct}%`, top: `${topPct}%`, width: `${wPct}%`, height: `${hPct}%` }}
                                  draggable
                                  onClick={(e) => { e.stopPropagation(); setSelectedArtworkId(art.element_id); }}
                                  onDragStart={(e) => {
                                    setDraggingArtworkItem({ ...art, isFromInventory: false });
                                    setIsDraggingArtwork(true);
                                    e.dataTransfer.setData('text/plain', art.element_id);
                                    e.dataTransfer.effectAllowed = 'move';
                                  }}
                                  title={`${art.title || 'Artwork'} — ${art.width}"×${art.height}" — drag or click + arrows`}
                                >
                                  <img src={getImgUrl(art, 'display')} alt={art.title || ''} draggable={false} />
                                  <button
                                    className="surface-artwork-remove"
                                    onClick={(ev) => { ev.stopPropagation(); handleRemoveArtworkFromTarget(art.element_id); }}
                                    title="Remove from wall"
                                  >×</button>
                                  {isSelected && (
                                    <div className="surface-nudge-controls">
                                      <button className="surface-nudge-btn nudge-up" onClick={(ev) => { ev.stopPropagation(); handleNudgeArtwork(art.element_id, freshWall.element_id, 0, ev.shiftKey ? -0.25 : -1); }} title={`Move up 1" (Shift = 0.25")`}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-8 8h5v8h6v-8h5z"/></svg></button>
                                      <button className="surface-nudge-btn nudge-down" onClick={(ev) => { ev.stopPropagation(); handleNudgeArtwork(art.element_id, freshWall.element_id, 0, ev.shiftKey ? 0.25 : 1); }} title={`Move down 1" (Shift = 0.25")`}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 20l8-8h-5v-8h-6v8h-5z"/></svg></button>
                                      <button className="surface-nudge-btn nudge-left" onClick={(ev) => { ev.stopPropagation(); handleNudgeArtwork(art.element_id, freshWall.element_id, ev.shiftKey ? -0.25 : -1, 0); }} title={`Move left 1" (Shift = 0.25")`}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M4 12l8-8v5h8v6h-8v5z"/></svg></button>
                                      <button className="surface-nudge-btn nudge-right" onClick={(ev) => { ev.stopPropagation(); handleNudgeArtwork(art.element_id, freshWall.element_id, ev.shiftKey ? 0.25 : 1, 0); }} title={`Move right 1" (Shift = 0.25")`}><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 12l-8 8v-5h-8v-6h8v-5z"/></svg></button>
                                    </div>
                                  )}
                                  {isSelected && (
                                    <div className="surface-artwork-dims-overlay">
                                      <span className="artwork-dims-text">{art.width}"w × {art.height}"h</span>
                                      <button className="artwork-swap-btn" onClick={(ev) => { ev.stopPropagation(); handleSwapArtworkDimensions(art); }} title="Swap width ↔ height">⇄ Swap W/H</button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {wallArt.length === 0 && (
                              <div className="surface-drop-hint">Drop artwork here</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  );
                })()}

                {/* Inventory dock at bottom of full-screen */}
                <div className="art-placement-inventory-dock">
                  <div className="inventory-dock-header">
                    <span className="inventory-dock-title">
                      {showArtistSearch ? 'Find Artist' : `Inventory (${unplacedArtworks.length} unplaced)`}
                    </span>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      {!showArtistSearch && (inventoryData?.accepted?.length > 0 || inventoryData?.pending?.length > 0) && (
                        <>
                          <button
                            onClick={() => setInventoryTab('accepted')}
                            className={`inventory-tab-btn ${inventoryTab === 'accepted' ? 'active-accepted' : ''}`}
                          >
                            ✓ Accepted ({inventoryData?.totalAccepted || 0})
                          </button>
                          <button
                            onClick={() => setInventoryTab('pending')}
                            className={`inventory-tab-btn ${inventoryTab === 'pending' ? 'active-pending' : ''}`}
                          >
                            ? Pending ({inventoryData?.totalPending || 0})
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => { setShowArtistSearch(!showArtistSearch); setSearchStatusMsg(null); }}
                        className={`inventory-tab-btn ${showArtistSearch ? 'active-search' : ''}`}
                      >
                        🔍 Find Artist
                      </button>
                    </div>
                    {!showArtistSearch && (
                      <input
                        type="text"
                        className="inventory-dock-search"
                        placeholder="Search by title or artist..."
                        value={inventorySearch}
                        onChange={(e) => setInventorySearch(e.target.value)}
                      />
                    )}
                  </div>

                  {showArtistSearch ? (
                    /* ── Artist Search Panel ── */
                    <div className="inventory-dock-search-panel">
                      <form onSubmit={handleArtistSearch} className="artist-search-form">
                        <input
                          type="text"
                          value={artistSearchQuery}
                          onChange={(e) => setArtistSearchQuery(e.target.value)}
                          placeholder="Name, email, or handle..."
                          className="artist-search-input"
                        />
                        <button type="submit" disabled={isSearchingArtist || !artistSearchQuery.trim()} className="artist-search-submit">
                          {isSearchingArtist ? '...' : 'Search'}
                        </button>
                        {artistSearchResults && (
                          <button type="button" onClick={clearArtistSearch} className="artist-search-clear">Clear</button>
                        )}
                      </form>

                      {searchStatusMsg && <div className="search-status-msg">{searchStatusMsg}</div>}

                      {artistSearchResults && artistSearchResults.allArtists?.length > 1 && (
                        <div className="artist-multi-results">
                          <span>{artistSearchResults.allArtists.length} artists found:</span>
                          <div className="artist-chips">
                            {artistSearchResults.allArtists.map(artist => (
                              <button
                                key={artist.id}
                                className={`artist-chip ${artistSearchResults.artist?.id === artist.id ? 'artist-chip-active' : ''}`}
                                onClick={async () => {
                                  try {
                                    setIsSearchingArtist(true);
                                    setArtworkFilterQuery('');
                                    const results = await getArtistArtworkByUuid(artist.uuid, artist);
                                    setArtistSearchResults({ ...results, allArtists: artistSearchResults.allArtists });
                                  } catch (err) {
                                    console.error('Error loading artist:', err);
                                  } finally {
                                    setIsSearchingArtist(false);
                                  }
                                }}
                              >
                                {artist.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {artistSearchResults?.artist && (
                        <div className="artist-info-card">
                          <div>
                            <strong>{artistSearchResults.artist.name}</strong>
                            {artistSearchResults.artist.handle && <span className="artist-handle"> @{artistSearchResults.artist.handle}</span>}
                            <div className="artist-artwork-count">{artistSearchResults.artworks.length} artwork{artistSearchResults.artworks.length !== 1 ? 's' : ''} for sale</div>
                          </div>
                        </div>
                      )}

                      {artistSearchResults?.artworks?.length > 5 && (
                        <input
                          type="text"
                          value={artworkFilterQuery}
                          onChange={(e) => setArtworkFilterQuery(e.target.value)}
                          placeholder="Filter by title..."
                          className="artwork-filter-input"
                        />
                      )}

                      <div className="artist-artwork-list">
                        {(artistSearchResults?.artworks || [])
                          .filter(a => !artworkFilterQuery.trim() || a.title?.toLowerCase().includes(artworkFilterQuery.toLowerCase()))
                          .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
                          .map(artwork => {
                            const alreadyAdded = isArtworkInCollection(artwork.vc_id);
                            return (
                              <div key={artwork.vc_id} className="artist-artwork-row">
                                <div className="artist-artwork-thumb" style={{ backgroundImage: `url(${getOptimizedImageUrl ? getOptimizedImageUrl(artwork.cover_image, 'thumbnail') : artwork.cover_image})` }} />
                                <div className="artist-artwork-info">
                                  <span className="artist-artwork-title">{artwork.title || 'Untitled'}</span>
                                  <span className="artist-artwork-meta">{artwork.width}"×{artwork.height}" • ${parseFloat(artwork.price || 0).toLocaleString()}</span>
                                </div>
                                <button
                                  onClick={() => handleAddArtworkToCollection(artwork)}
                                  disabled={alreadyAdded || addingArtworkId === artwork.vc_id}
                                  className={`artist-artwork-add-btn ${alreadyAdded ? 'already-added' : ''}`}
                                >
                                  {addingArtworkId === artwork.vc_id ? '...' : alreadyAdded ? 'Added' : '+ Add'}
                                </button>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ) : (
                    /* ── Standard Inventory Items ── */
                    <div className="inventory-dock-items">
                      {filteredUnplaced.length === 0 && (
                        <div className="inventory-dock-empty">
                          {inventorySearch ? 'No matching artworks' : 'All artworks have been placed'}
                        </div>
                      )}
                      {filteredUnplaced.map(art => (
                        <div
                          key={art.vc_id || art.element_id}
                          className={`inventory-dock-item ${art.accepted === false ? 'inventory-dock-item-pending' : ''}`}
                          draggable
                          onDragStart={(e) => {
                            setDraggingArtworkItem({ ...art, isFromInventory: true });
                            setIsDraggingArtwork(true);
                            e.dataTransfer.setData('text/plain', art.vc_id || art.element_id);
                            e.dataTransfer.effectAllowed = 'copy';
                          }}
                          onDragEnd={() => { setDraggingArtworkItem(null); setIsDraggingArtwork(false); }}
                          title={`${art.title || 'Untitled'} — ${art.artist_name || 'Unknown'} — ${art.width || '?'}"×${art.height || '?'}"`}
                        >
                          <div className="inventory-dock-thumb-wrapper">
                            <img src={getImgUrl(art, 'thumbnail')} alt={art.title || ''} draggable={false} />
                            {art.accepted === true && <span className="inventory-dock-badge badge-accepted">✓</span>}
                            {art.accepted === false && <span className="inventory-dock-badge badge-pending">?</span>}
                          </div>
                          <div className="inventory-dock-item-info">
                            <span className="inventory-dock-item-title">{art.title || 'Untitled'}</span>
                            <span className="inventory-dock-item-artist">{art.artist_name || ''}</span>
                            <span className="inventory-dock-item-dims">{art.width || '?'}" × {art.height || '?'}"</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        
        {/* Add Wall Modal */}
        {showAddWallModal && (
          <div className="position-editor-modal">
            <div className="position-editor-content">
              <h3>Add New Wall</h3>
              
              <div className="position-inputs">
                <div className="input-group">
                  <label>Wall Name:</label>
                  <input
                    type="text"
                    value={newWallData.name}
                    onChange={(e) => setNewWallData({
                      ...newWallData,
                      name: e.target.value
                    })}
                    placeholder="e.g., Main Wall, Hanging Panel"
                  />
                  <small>Give your wall a descriptive name</small>
                </div>
                
                <div className="input-group">
                  <label>Width (inches):</label>
                  <input
                    type="number"
                    value={newWallData.width}
                    onChange={(e) => setNewWallData({
                      ...newWallData,
                      width: parseFloat(e.target.value) || 120
                    })}
                    step="1"
                    min="12"
                  />
                  <small>Wall width in inches</small>
                </div>
                
                <div className="input-group">
                  <label>Height (inches):</label>
                  <input
                    type="number"
                    value={newWallData.height}
                    onChange={(e) => setNewWallData({
                      ...newWallData,
                      height: parseFloat(e.target.value) || 96
                    })}
                    step="1"
                    min="12"
                  />
                  <small>Wall height in inches</small>
                </div>
                
                <div className="input-group">
                  <label>Center X (inches):</label>
                  <input
                    type="number"
                    value={newWallData.centerX}
                    onChange={(e) => setNewWallData({
                      ...newWallData,
                      centerX: parseFloat(e.target.value) || 0
                    })}
                    step="1"
                  />
                  <small>Left/Right position (negative = left, positive = right)</small>
                </div>
                
                <div className="input-group">
                  <label>Center Z (inches):</label>
                  <input
                    type="number"
                    value={newWallData.centerZ}
                    onChange={(e) => setNewWallData({
                      ...newWallData,
                      centerZ: parseFloat(e.target.value) || 0
                    })}
                    step="1"
                  />
                  <small>Front/Back position (negative = back, positive = front)</small>
                </div>
                
                <div className="input-group">
                  <label>Rotation Y (degrees):</label>
                  <input
                    type="number"
                    value={newWallData.rotationY}
                    onChange={(e) => setNewWallData({
                      ...newWallData,
                      rotationY: parseFloat(e.target.value) || 0
                    })}
                    step="90"
                    min="0"
                    max="360"
                  />
                  <small>Wall orientation (0°, 90°, 180°, 270°)</small>
                </div>
              </div>
              
              <div className="position-editor-actions">
                <button 
                  className="save-btn"
                  onClick={handleAddWall}
                >
                  <Plus size={16} />
                  Add Wall
                </button>
                <button 
                  className="cancel-btn"
                  onClick={() => setShowAddWallModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Add Pod Modal */}
        {showAddPodModal && (
          <div className="position-editor-modal">
            <div className="position-editor-content">
              <h3>Add Pod (Tent Panel)</h3>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 12px' }}>A single tilted wall panel — one side of an A-frame tent structure (PVC pipe + canvas)</p>
              
              <div className="position-inputs">
                <div className="input-group">
                  <label>Pod Name:</label>
                  <input
                    type="text"
                    value={newPodData.name}
                    onChange={(e) => setNewPodData({ ...newPodData, name: e.target.value })}
                    placeholder="e.g., Tent-A Left, Pod1"
                  />
                </div>
                
                <div className="input-row">
                  <div className="input-group">
                    <label>Wall Width (inches):</label>
                    <input
                      type="number"
                      value={newPodData.wallWidth}
                      onChange={(e) => setNewPodData({ ...newPodData, wallWidth: parseFloat(e.target.value) || 96 })}
                      step="12"
                      min="24"
                    />
                  </div>
                  <div className="input-group">
                    <label>Wall Height (inches):</label>
                    <input
                      type="number"
                      value={newPodData.wallHeight}
                      onChange={(e) => setNewPodData({ ...newPodData, wallHeight: parseFloat(e.target.value) || 96 })}
                      step="12"
                      min="48"
                    />
                  </div>
                </div>
                
                <div className="input-group">
                  <label>Tilt Angle (degrees from floor):</label>
                  <input
                    type="number"
                    value={newPodData.tiltAngle}
                    onChange={(e) => setNewPodData({ ...newPodData, tiltAngle: parseFloat(e.target.value) || 70 })}
                    step="5"
                    min="30"
                    max="90"
                  />
                  <small>
                    {newPodData.tiltAngle === 90 ? 'Vertical (flat wall)' : `${newPodData.tiltAngle}° lean — floor depth: ${(newPodData.wallHeight * Math.cos((newPodData.tiltAngle * Math.PI) / 180)).toFixed(1)}" | peak height: ${(newPodData.wallHeight * Math.sin((newPodData.tiltAngle * Math.PI) / 180)).toFixed(1)}"`}
                  </small>
                </div>
                
                <div className="input-row">
                  <div className="input-group">
                    <label>Base Center X (inches):</label>
                    <input
                      type="number"
                      value={newPodData.centerX}
                      onChange={(e) => setNewPodData({ ...newPodData, centerX: parseFloat(e.target.value) || 0 })}
                      step="1"
                    />
                  </div>
                  <div className="input-group">
                    <label>Base Center Z (inches):</label>
                    <input
                      type="number"
                      value={newPodData.centerZ}
                      onChange={(e) => setNewPodData({ ...newPodData, centerZ: parseFloat(e.target.value) || 0 })}
                      step="1"
                    />
                  </div>
                </div>
                
                <div className="input-group">
                  <label>Facing Direction (degrees):</label>
                  <input
                    type="number"
                    value={newPodData.rotationY}
                    onChange={(e) => setNewPodData({ ...newPodData, rotationY: parseFloat(e.target.value) || 0 })}
                    step="15"
                    min="0"
                    max="360"
                  />
                  <small>Direction the artwork side faces (0° = toward viewer)</small>
                </div>
              </div>
              
              <div className="position-editor-actions">
                <button className="save-btn" onClick={handleAddPod}>
                  <Plus size={16} />
                  Add Pod
                </button>
                <button className="cancel-btn" onClick={() => setShowAddPodModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Add Easel Modal */}
        {showAddEaselModal && (
          <div className="position-editor-modal">
            <div className="position-editor-content">
              <h3>Add New Easel</h3>
              
              <div className="position-inputs">
                <div className="input-group">
                  <label>Easel Name:</label>
                  <input
                    type="text"
                    value={newEaselData.name}
                    onChange={(e) => setNewEaselData({ ...newEaselData, name: e.target.value })}
                    placeholder="e.g., Front Easel, Entry Easel"
                  />
                </div>
                
                <div className="input-row">
                  <div className="input-group">
                    <label>Position X (inches):</label>
                    <input
                      type="number"
                      value={newEaselData.centerX}
                      onChange={(e) => setNewEaselData({ ...newEaselData, centerX: parseFloat(e.target.value) || 0 })}
                      step="1"
                    />
                  </div>
                  <div className="input-group">
                    <label>Position Z (inches):</label>
                    <input
                      type="number"
                      value={newEaselData.centerZ}
                      onChange={(e) => setNewEaselData({ ...newEaselData, centerZ: parseFloat(e.target.value) || 0 })}
                      step="1"
                    />
                  </div>
                </div>
                
                <div className="input-group">
                  <label>Facing Direction (degrees):</label>
                  <input
                    type="number"
                    value={newEaselData.rotationY}
                    onChange={(e) => setNewEaselData({ ...newEaselData, rotationY: parseFloat(e.target.value) || 0 })}
                    step="15"
                    min="0"
                    max="360"
                  />
                </div>
              </div>
              
              <div className="position-editor-actions">
                <button className="save-btn" onClick={handleAddEasel}>
                  <Plus size={16} />
                  Add Easel
                </button>
                <button className="cancel-btn" onClick={() => setShowAddEaselModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Add Structure Modal */}
        {showAddStructureModal && (
          <div className="position-editor-modal">
            <div className="position-editor-content">
              <h3>Add Structure</h3>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 12px' }}>Columns and solid walls that appear in 3D but cannot hold artwork</p>
              
              <div className="position-inputs">
                <div className="input-group">
                  <label>Name:</label>
                  <input
                    type="text"
                    value={newStructureData.name}
                    onChange={(e) => setNewStructureData({ ...newStructureData, name: e.target.value })}
                    placeholder="e.g., Entry Column, Divider Wall"
                  />
                </div>
                
                <div className="input-group">
                  <label>Shape:</label>
                  <select
                    value={newStructureData.shape}
                    onChange={(e) => {
                      const shape = e.target.value;
                      let defaults = {};
                      if (shape === 'column-square') defaults = { width: 12, depth: 12 };
                      else if (shape === 'column-round') defaults = { width: 18, depth: 18 };
                      else if (shape === 'column-rect') defaults = { width: 12, depth: 24 };
                      else if (shape === 'wall-solid') defaults = { width: 120, depth: 6 };
                      setNewStructureData({ ...newStructureData, shape, ...defaults });
                    }}
                    style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  >
                    <option value="column-square">Square Column</option>
                    <option value="column-round">Round Column</option>
                    <option value="column-rect">Rectangular Column</option>
                    <option value="wall-solid">Solid Wall (no artwork)</option>
                  </select>
                </div>
                
                <div className="input-row">
                  <div className="input-group">
                    <label>{newStructureData.shape === 'wall-solid' ? 'Length' : 'Width'} (inches):</label>
                    <input
                      type="number"
                      value={newStructureData.width}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 12;
                        const updates = { width: val };
                        if (newStructureData.shape === 'column-square' || newStructureData.shape === 'column-round') updates.depth = val;
                        setNewStructureData({ ...newStructureData, ...updates });
                      }}
                      step="1"
                      min="4"
                    />
                  </div>
                  {(newStructureData.shape === 'column-rect' || newStructureData.shape === 'wall-solid') && (
                    <div className="input-group">
                      <label>Depth (inches):</label>
                      <input
                        type="number"
                        value={newStructureData.depth}
                        onChange={(e) => setNewStructureData({ ...newStructureData, depth: parseFloat(e.target.value) || 6 })}
                        step="1"
                        min="4"
                      />
                    </div>
                  )}
                </div>
                
                <div className="input-group">
                  <label>Height (inches):</label>
                  <input
                    type="number"
                    value={newStructureData.height}
                    onChange={(e) => setNewStructureData({ ...newStructureData, height: parseFloat(e.target.value) || 96 })}
                    step="1"
                    min="12"
                  />
                  <small>{(newStructureData.height / 12).toFixed(1)} ft tall</small>
                </div>
                
                <div className="input-row">
                  <div className="input-group">
                    <label>Position X:</label>
                    <input
                      type="number"
                      value={newStructureData.centerX}
                      onChange={(e) => setNewStructureData({ ...newStructureData, centerX: parseFloat(e.target.value) || 0 })}
                      step="1"
                    />
                  </div>
                  <div className="input-group">
                    <label>Position Z:</label>
                    <input
                      type="number"
                      value={newStructureData.centerZ}
                      onChange={(e) => setNewStructureData({ ...newStructureData, centerZ: parseFloat(e.target.value) || 0 })}
                      step="1"
                    />
                  </div>
                </div>
                
                <div className="input-row">
                  <div className="input-group">
                    <label>Rotation:</label>
                    <input
                      type="number"
                      value={newStructureData.rotationY}
                      onChange={(e) => setNewStructureData({ ...newStructureData, rotationY: parseFloat(e.target.value) || 0 })}
                      step="15"
                      min="0"
                      max="360"
                    />
                  </div>
                  <div className="input-group">
                    <label>Color:</label>
                    <input
                      type="color"
                      value={newStructureData.color}
                      onChange={(e) => setNewStructureData({ ...newStructureData, color: e.target.value })}
                      style={{ width: '100%', height: '32px', padding: '2px', cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="position-editor-actions">
                <button className="save-btn" onClick={handleAddStructure}>
                  <Plus size={16} />
                  Add Structure
                </button>
                <button className="cancel-btn" onClick={() => setShowAddStructureModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopDownWallPlanner;
